import React, { useEffect, useState, useCallback } from "react";
import ProcedureMoveAppBar from "../../../../../common/ProcedureMoveAppBar";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuditTrailByid,
  getAuditTrailByPreview,
} from "../../../../../redux/auditTrailSlice";
import { getAllVersionHistories } from "../../../../../redux/versionHistorySlice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AccentReadOnlyCheckbox from "../../../../../common/FormFields/AccentReadOnlyCheckbox";
import ProcedureStatusInfo from "./ProcedureStatusInfo";
import PreviewContent from "../../components/PreviewContent";
import Loader from "../../../../../common/Loader";
import {
  getRichTextCategoryDisplayValue,
  normalizeRichTextFieldForRenderHtml,
  processContentData,
} from "../../utils/previewUtils";
import { SNAPSHOT_FETCH_INIT } from "../../utils/fetchSnapshotContent";
import {
  parseAuditSectionCopyMoveLabels,
  parseAuditStepSaveActionLabels,
  parseAuditSubstepCopyMoveSourceLabels,
  parseAuditSubstepSaveEditLabels,
  sortSectionsForSnapshotDisplay,
} from "../../utils/sectionDisplayOrdinal";
import {
  narrowProcedureTreeBySnapshotId,
  resolveSnapshotNodeTypeForAudit,
  resolveSnapshotTreeNodeIdForAudit,
} from "../../utils/auditTrailNarrowTree";
import VersionHistoryPopup from "../../../../../common/VersionHistoryPopup/VersionHistoryPopup";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import "../../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme.css";
import "../../../../../common/RichTextEditor/LexicalEditor/ui/ContentEditable.css";
import "../../../../RichText/RichText.css";
import { renderPreview } from "../../../../SectionElements/plugins/utils";

/** IDs from audit API version_content used to find the matching node in snapshot JSON */
function collectAuditTargetTreeIds(vc) {
  if (!vc || typeof vc !== "object") return [];
  return [
    vc.preview_tree_node_id,
    vc.tree_node_id,
    vc.procedure_tree_ref,
    vc.section_tree_node_id,
    vc.section_id_ref,
    vc.step_tree_node_id,
    vc.step_id_ref,
    vc.sub_step_tree_node_id,
    vc.substep_tree_node_id,
    vc.substep_id_ref,
    vc.video_tree_node_id,
    vc.canvas_tree_node_id,
    vc.canva_tree_node_id,
    vc.rich_text_node_id,
    vc.rich_text_tree_node_id,
    vc.attachment_tree_node_id,
  ].filter(Boolean);
}

function nodeMatchesAuditTargets(node, targetSet) {
  if (!node || !targetSet.size) return false;
  const candidates = [
    node.tree_node_id,
    node.procedure_tree_ref,
    node.section_id_ref,
    node.step_id_ref,
    node.substep_id_ref,
    node.detail?.section_tree_node_id,
    node.detail?.step_tree_node_id,
    node.detail?.sub_step_tree_node_id,
    node.detail?.attachment_tree_node_id,
    node.detail?.preview_tree_node_id,
    node.detail?.canvas_tree_node_id,
    node.detail?.canva_tree_node_id,
    node.detail?.procedure_tree_ref,
    node.detail?.video_tree_node_id,
    node.detail?.rich_text_node_id,
    node.detail?.rich_text_tree_node_id,
  ].filter(Boolean);
  return candidates.some((id) => targetSet.has(id));
}

/** Walk snapshot tree rows and return tree_node_id values that match audit target ids */
function collectResolvedTreeIdsFromSnapshot(sections, targetIds) {
  const targetSet = new Set(targetIds);
  const resolved = [];
  const visitStep = (sec, st) => {
    if (!st) return;
    if (nodeMatchesAuditTargets(st, targetSet) && st.tree_node_id) {
      resolved.push(st.tree_node_id);
      /* Step-level audit (e.g. bulk copy step): check parent section too so Section row matches header. */
      if (sec?.tree_node_id) resolved.push(sec.tree_node_id);
    }
    (st.children || []).forEach((ch) => {
      if (nodeMatchesAuditTargets(ch, targetSet) && ch.tree_node_id) {
        resolved.push(ch.tree_node_id);
        /* CANVA_/CANVAS_/ATTACHMENT_/… audit often only has preview_tree_node_id on the child; Step checkbox uses parent step.tree_node_id */
        if (st.tree_node_id) resolved.push(st.tree_node_id);
        /* Do not push sec.tree_node_id — it marks the whole section checkbox for one child focus */
      }
    });
    (st.substeps || []).forEach((ss) => {
      if (nodeMatchesAuditTargets(ss, targetSet) && ss.tree_node_id) {
        resolved.push(ss.tree_node_id);
        if (st.tree_node_id) resolved.push(st.tree_node_id);
        if (sec?.tree_node_id) resolved.push(sec.tree_node_id);
      }
      (ss.children || []).forEach((ch) => {
        if (nodeMatchesAuditTargets(ch, targetSet) && ch.tree_node_id) {
          resolved.push(ch.tree_node_id);
          if (ss.tree_node_id) resolved.push(ss.tree_node_id);
          if (st.tree_node_id) resolved.push(st.tree_node_id);
        }
      });
    });
  };
  for (const sec of sections || []) {
    if (nodeMatchesAuditTargets(sec, targetSet) && sec.tree_node_id) {
      resolved.push(sec.tree_node_id);
    }
    (sec.steps || []).forEach((st) => visitStep(sec, st));
  }
  return [...new Set(resolved)];
}

/** Normalize action_to_show for comparisons (e.g. VIDEO-MOVED, RICH-TEXT_MOVED → underscore form). */
function normalizeActionTokens(actionToShow) {
  if (!actionToShow || typeof actionToShow !== "string") return "";
  return actionToShow
    .toUpperCase()
    .replace(/-/g, "_")
    /* Human audit lines use "Rich Text" with a space — fold so RICH_TEXT_* / inferBulkMovedNodeType match */
    .replace(/\bRICH\s+TEXT\b/g, "RICH_TEXT");
}

/** Map SECTION_MOVED / STEP_MOVED / … from audit API to snapshot node type for narrowing. */
function inferBulkMovedNodeType(actionToShow) {
  if (!actionToShow || typeof actionToShow !== "string") return undefined;
  const u = normalizeActionTokens(actionToShow);
  if (u.includes("SECTION_MOVED") || u.includes("SECTION_COPIED")) return "section";
  if (u.includes("SUBSTEP_MOVED") || u.includes("SUBSTEP_COPIED")) return "substep";
  if (u.includes("STEP_MOVED") || u.includes("STEP_COPIED")) return "step";
  /* Snapshot root version_content often uses human text ("User Copied Section 3 …") not SECTION_COPIED */
  if (
    /\b(COPIED|MOVED)\s+SECTION\b/i.test(actionToShow) ||
    (/\bSECTION\s+\d+/i.test(actionToShow) &&
      /\b(COPIED|MOVED)\b/i.test(actionToShow))
  ) {
    return "section";
  }
  /* Human substep / step copy-move when tokens STEP_COPIED / SUBSTEP_COPIED are omitted */
  if (
    /\b(COPIED|MOVED)\s+SUBSTEP\b/i.test(actionToShow) ||
    (/\bSUBSTEP\s+\d+/i.test(actionToShow) &&
      /\b(COPIED|MOVED)\b/i.test(actionToShow))
  ) {
    return "substep";
  }
  if (
    /\b(COPIED|MOVED)\s+STEP\b/i.test(actionToShow) ||
    (/\bSTEP\s+\d+/i.test(actionToShow) &&
      /\b(COPIED|MOVED)\b/i.test(actionToShow))
  ) {
    return "step";
  }
  if (u.includes("ATTACHMENT")) return "attachment";
  if (u.includes("CANVAS") || u.includes("CANVA")) return "canvas";
  if (u.includes("RICH_TEXT")) return "rich-text";
  if (u.includes("VIDEO")) return "video";
  return undefined;
}

/** Empty [] is truthy in JS — use length so copy/replace blocks with only newContent match. */
function bulkBlockHasRows(block) {
  if (!block) return false;
  const oc = block.originalContent;
  const nc = block.newContent;
  const ocOk = Array.isArray(oc) && oc.length > 0;
  const ncOk = Array.isArray(nc) && nc.length > 0;
  return ocOk || ncOk;
}

/**
 * View Original / View New for UUID bulk blocks. When View Original and originalContent is empty
 * (e.g. copy step — "before" empty) but newContent has the tree, use newContent so the preview shows data.
 */
function pickBulkBlockRowsForView(block, viewType) {
  if (!block) return [];
  const oc = Array.isArray(block.originalContent) ? block.originalContent : [];
  const nc = Array.isArray(block.newContent) ? block.newContent : [];
  if (viewType === "new") {
    return nc.length > 0 ? nc : oc;
  }
  if (oc.length > 0) return oc;
  if (nc.length > 0) return nc;
  return [];
}

const BULK_BLOCK_UUID_KEY =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** API / snapshot may send main_entry as boolean or string */
function isTruthyMainEntry(v) {
  if (v === true) return true;
  if (typeof v === "string" && v.trim().toLowerCase() === "true") return true;
  return false;
}

/**
 * Merge audit row `version_content` with nested `version_content` when both exist, so fields like
 * `preview_tree_node_id` on the outer object are not lost when the API also sends an inner envelope.
 */
function mergeAuditVersionContent(row) {
  const outer = row?.version_content;
  if (!outer || typeof outer !== "object" || Array.isArray(outer)) return null;
  const inner = outer.version_content;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return { ...outer, ...inner };
  }
  return outer;
}

/**
 * Merge current audit row VC with parent_version_data VC (child overrides parent).
 * Ensures preview_tree_node_id is found when the API only sends it on the parent row.
 */
function mergeAuditTrailRowVersionContentDeep(auditTrailRow) {
  if (!auditTrailRow || typeof auditTrailRow !== "object") return null;
  const child = mergeAuditVersionContent(auditTrailRow);
  const parentRow = auditTrailRow.parent_version_data;
  const parent =
    parentRow && typeof parentRow === "object"
      ? mergeAuditVersionContent(parentRow)
      : null;
  if (parent && child) return { ...parent, ...child };
  return child || parent;
}

/** Node / element `type` on version_content — not procedure category labels (e.g. SUBASSEMBLY). */
const VC_STRUCTURAL_TYPE_LABELS = new Set([
  "step",
  "substep",
  "section",
  "canvas",
  "canva",
  "attachment",
  "rich-text",
  "rich_text",
  "video",
]);

/**
 * Procedure header line is item_code_type + part number. Some payloads only set `type` (SUBASSEMBLY);
 * child-node audits may set `type` to canvas/step — ignore those. Matches PreviewLayout `item_code_type || type`.
 */
function procedureCategoryLabelFromVc(vc) {
  if (!vc || typeof vc !== "object") return undefined;
  if (vc.item_code_type) return vc.item_code_type;
  const t = vc.type;
  if (typeof t !== "string" || !t.trim()) return undefined;
  if (VC_STRUCTURAL_TYPE_LABELS.has(t.trim().toLowerCase())) return undefined;
  return t;
}

/**
 * Resolve bulk_audit_trail block for a focus id (typically audit preview_tree_node_id):
 * 1) If data[focusId] exists and has rows, use it (block key === procedure tree id).
 * 2) Else walk top-level keys in snapshot order; return the first UUID block whose originalContent
 *    or newContent tree contains a node matching focusId (tree_node_id, procedure_tree_ref, entity ids).
 * Copy/replace snapshots may include first_entry plus per-operation UUID keys — UUID blocks are scanned first.
 * @returns {{ key: string, block: object } | null}
 */
function findBulkAuditTrailBlockWithKey(data, focusId) {
  if (!focusId || !data || typeof data !== "object") return null;
  const direct = data[focusId];
  if (direct && bulkBlockHasRows(direct)) return { key: focusId, block: direct };

  const nodeMatches = (node) => {
    if (!node || !focusId) return false;
    if (node.tree_node_id === focusId) return true;
    if (node.preview_tree_node_id === focusId) return true;
    const d = node.detail || {};
    if (d.preview_tree_node_id === focusId) return true;
    if (d.procedure_tree_ref === focusId) return true;
    if (node.type === "section") {
      if (d.section_tree_node_id === focusId) return true;
      if (node.section_id_ref === focusId) return true;
    }
    if (node.type === "step") {
      if (d.step_tree_node_id === focusId) return true;
      if (d.step_id_ref === focusId) return true;
      if (d.procedure_tree_ref === focusId) return true;
      if (node.step_id_ref === focusId) return true;
    }
    if (node.type === "substep") {
      if (
        d.sub_step_tree_node_id === focusId ||
        node.substep_id_ref === focusId ||
        d.procedure_tree_ref === focusId
      ) {
        return true;
      }
    }
    if (
      (node.type === "rich-text" ||
        node.type === "attachment" ||
        node.type === "video" ||
        node.type === "canva" ||
        node.type === "canvas") &&
      (d.rich_text_node_id === focusId ||
        d.attachment_tree_node_id === focusId ||
        d.video_tree_node_id === focusId ||
        d.canvas_tree_node_id === focusId ||
        d.canva_tree_node_id === focusId ||
        d.procedure_tree_ref === focusId)
    ) {
      return true;
    }
    return false;
  };

  const treeContainsFocus = (sections) => {
    if (!Array.isArray(sections)) return false;
    for (const sec of sections) {
      if (nodeMatches(sec)) return true;
      for (const st of sec.steps || []) {
        if (nodeMatches(st)) return true;
        for (const ch of st.children || []) {
          if (nodeMatches(ch)) return true;
        }
        for (const ss of st.substeps || []) {
          if (nodeMatches(ss)) return true;
          for (const ch of ss.children || []) {
            if (nodeMatches(ch)) return true;
          }
        }
      }
    }
    return false;
  };

  const tryBlock = (block) => {
    if (!block || !bulkBlockHasRows(block)) return false;
    const oc = block.originalContent;
    const nc = block.newContent;
    return treeContainsFocus(oc) || treeContainsFocus(nc);
  };

  const keys = Object.keys(data);
  for (const key of keys) {
    if (key === "version_content" || key === "first_entry") continue;
    if (!BULK_BLOCK_UUID_KEY.test(key)) continue;
    const block = data[key];
    if (tryBlock(block)) return { key, block };
  }
  if (data.first_entry && tryBlock(data.first_entry)) {
    return { key: "first_entry", block: data.first_entry };
  }
  for (const key of keys) {
    if (key === "version_content" || key === "first_entry") continue;
    if (BULK_BLOCK_UUID_KEY.test(key)) continue;
    const block = data[key];
    if (tryBlock(block)) return { key, block };
  }
  return null;
}

function findBulkAuditTrailBlock(data, focusId) {
  const r = findBulkAuditTrailBlockWithKey(data, focusId);
  return r?.block ?? null;
}

/**
 * Top-level key of the first bulk block in snapshot order whose originalContent/newContent trees contain focusId.
 * Used after scanning new_content_snapshot so previous_content_snapshot can reuse the same block key.
 */
function findBulkAuditTrailBlockKey(data, focusId) {
  const r = findBulkAuditTrailBlockWithKey(data, focusId);
  return r?.key ?? null;
}

/**
 * When version_content.main_entry is not true: id used to locate the correct UUID-keyed block.
 * Prefer `preview_tree_node_id` (after merge with nested version_content + parent_version_data), then other ids.
 * That block's originalContent -> View Original, newContent -> View New (see fetchSnapshotData viewType).
 */
function getAuditFocusIdForBulkBlock(auditTrailRow) {
  const vc = mergeAuditTrailRowVersionContentDeep(auditTrailRow);
  if (!vc || typeof vc !== "object") return null;
  return (
    vc.preview_tree_node_id ||
    vc.tree_node_id ||
    vc.section_tree_node_id ||
    vc.section_id_ref ||
    vc.step_tree_node_id ||
    vc.step_id_ref ||
    vc.sub_step_tree_node_id ||
    vc.substep_tree_node_id ||
    vc.canvas_tree_node_id ||
    vc.canva_tree_node_id ||
    vc.rich_text_node_id ||
    vc.rich_text_tree_node_id ||
    vc.attachment_tree_node_id ||
    vc.video_tree_node_id ||
    null
  );
}

/**
 * Single-audit snapshot envelope `{ data: [...] }` has no UUID bulk blocks; when API omits main_entry
 * and only sends preview_tree_node_id, find the first tree node matching that id (top-down) and
 * return a minimal section array for RevisionPreview (same shapes as PreviewContent focus).
 */
function narrowProcedureTreeRowsToFirstFocusId(sections, focusId) {
  if (!focusId || !Array.isArray(sections)) return null;

  const nodeMatches = (node) => {
    if (!node || !focusId) return false;
    if (node.tree_node_id === focusId) return true;
    if (node.preview_tree_node_id === focusId) return true;
    const d = node.detail || {};
    if (d.preview_tree_node_id === focusId) return true;
    if (d.procedure_tree_ref === focusId) return true;
    if (node.type === "section") {
      if (d.section_tree_node_id === focusId) return true;
      if (node.section_id_ref === focusId) return true;
    }
    if (node.type === "step") {
      if (d.step_tree_node_id === focusId) return true;
      if (d.step_id_ref === focusId) return true;
      if (d.procedure_tree_ref === focusId) return true;
      if (node.step_id_ref === focusId) return true;
    }
    if (node.type === "substep") {
      if (
        d.sub_step_tree_node_id === focusId ||
        node.substep_id_ref === focusId ||
        d.procedure_tree_ref === focusId
      ) {
        return true;
      }
    }
    if (
      (node.type === "rich-text" ||
        node.type === "attachment" ||
        node.type === "video" ||
        node.type === "canva" ||
        node.type === "canvas") &&
      (d.rich_text_node_id === focusId ||
        d.attachment_tree_node_id === focusId ||
        d.video_tree_node_id === focusId ||
        d.canvas_tree_node_id === focusId ||
        d.canva_tree_node_id === focusId ||
        d.procedure_tree_ref === focusId)
    ) {
      return true;
    }
    return false;
  };

  for (const sec of sections) {
    if (!sec) continue;
    if (nodeMatches(sec)) {
      return [{ ...sec }];
    }
    for (const st of sec.steps || []) {
      if (nodeMatches(st)) {
        return [{ ...sec, hideSectionHeader: true, steps: [{ ...st }] }];
      }
      for (const ch of st.children || []) {
        if (nodeMatches(ch)) {
          return [
            {
              ...sec,
              hideSectionHeader: true,
              steps: [{ ...st, children: [ch], substeps: [] }],
            },
          ];
        }
      }
      for (const ss of st.substeps || []) {
        if (nodeMatches(ss)) {
          return [
            {
              ...sec,
              hideSectionHeader: true,
              steps: [{ ...st, substeps: [ss], children: [] }],
            },
          ];
        }
        for (const ch of ss.children || []) {
          if (nodeMatches(ch)) {
            return [
              {
                ...sec,
                hideSectionHeader: true,
                steps: [
                  {
                    ...st,
                    substeps: [{ ...ss, children: [ch] }],
                    children: [],
                  },
                ],
              },
            ];
          }
        }
      }
    }
  }
  return null;
}

/**
 * When version_content.main_entry is true, snapshots expose originalContent + newContent on one block.
 * Prefer the block whose key matches API tree ids (first tree_node_id), then first UUID key, then first_entry.
 * View Original → originalContent; View New → newContent (per fetchSnapshotData viewType).
 * Copied / moved Section (SECTION_COPIED): blocks may be keyed by section_tree_node_id or section_id_ref.
 * Copied / moved Step (STEP_COPIED): step_tree_node_id or step_id_ref.
 * Copied / moved Substep (SUBSTEP_COPIED, NODE_COPIED + human action): sub_step_tree_node_id / substep_tree_node_id.
 */
function findMainEntryBulkBlock(data, auditTrailRow) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const vc = mergeAuditTrailRowVersionContentDeep(auditTrailRow);
  const skipTop = (k) => k === "version_content" || k === "auditData";

  const keyCandidates = [
    ...new Set(
      [
        vc?.preview_tree_node_id,
        vc?.tree_node_id,
        vc?.section_tree_node_id,
        vc?.section_id_ref,
        vc?.step_tree_node_id,
        vc?.step_id_ref,
        vc?.sub_step_tree_node_id,
        vc?.substep_tree_node_id,
        vc?.substep_id_ref,
      ].filter(Boolean),
    ),
  ];

  for (const id of keyCandidates) {
    const block = data[id];
    if (block && bulkBlockHasRows(block)) return { key: id, block };
  }

  const uuidKeys = Object.keys(data).filter(
    (k) => !skipTop(k) && k !== "first_entry" && BULK_BLOCK_UUID_KEY.test(k),
  );
  for (const k of uuidKeys) {
    const block = data[k];
    if (block && bulkBlockHasRows(block)) return { key: k, block };
  }

  if (data.first_entry && bulkBlockHasRows(data.first_entry)) {
    return { key: "first_entry", block: data.first_entry };
  }

  for (const key of Object.keys(data)) {
    if (skipTop(key) || key === "first_entry") continue;
    if (BULK_BLOCK_UUID_KEY.test(key)) continue;
    const block = data[key];
    if (block && bulkBlockHasRows(block)) return { key, block };
  }
  return null;
}

const RevisionPreview = ({
  hideAppBar = false,
  externalSelectedView = null,
  externalSetSelectedView = null,
  externalAuditTrailData = null,
  /** When hideAppBar + external audit data, aligns tree checkbox emphasis with parent AppBar label (e.g. PreviewLayout). */
  procedureAppBarTitleBase = null,
}) => {
  const [internalSelectedView, setInternalSelectedView] = useState("new");
  const selectedView =
    externalSelectedView !== null ? externalSelectedView : internalSelectedView;
  const setSelectedView =
    externalSetSelectedView !== null
      ? externalSetSelectedView
      : setInternalSelectedView;

  const dispatch = useDispatch();
  const { audit_trail_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMoveCopyDeletePreview =
    location.pathname?.includes("move-copy-delete-preview");
  const [audit_trail_data, setAuditData] = useState({});
  const [originalContent, setOriginalContent] = useState({
    data: [],
    loaded: false,
    meta: null,
  });
  const [newContent, setNewContent] = useState({
    data: [],
    loaded: false,
    meta: null,
  });
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState({}); // Track which nodes are selected for display

  const { loading } = useSelector((state) => state.audittrail);

  useEffect(() => {
    setOriginalContent({ data: [], loaded: false, meta: null });
    setNewContent({ data: [], loaded: false, meta: null });
    setSelectedNodes({});
  }, [audit_trail_id]);

  // Get version history data from Redux store
  const { versionHistories, loading: isLoadingVersions } = useSelector(
    (state) => state.versionHistory,
  );

  /**
   * Fetches snapshot data from S3 URL and handles different formats.
   * @param {object} [options]
   * @param {string} [options.resolvedBlockKey] - UUID key from scanning new_content_snapshot so previous/new use the same block
   * @param {object} [options.prefetchedJson] - Skip fetch (e.g. reuse JSON already loaded for key resolution)
   */
  const fetchSnapshotData = async (
    snapshotUrl,
    viewType,
    auditTrailRow,
    options = {},
  ) => {
    if (!snapshotUrl && options.prefetchedJson == null) return null;

    try {
      let data;
      if (options.prefetchedJson != null) {
        data = options.prefetchedJson;
      } else {
        const response = await fetch(snapshotUrl, SNAPSHOT_FETCH_INIT);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        data = await response.json();
      }
      /** If main_entry + UUID-keyed bulk blocks exist, do not use root originalContent/newContent (may be empty); use findMainEntryBulkBlock (e.g. Copied Substep). */
      const vcMergedForEarlyBranch =
        mergeAuditTrailRowVersionContentDeep(auditTrailRow);
      const mainEntryEarly = isTruthyMainEntry(vcMergedForEarlyBranch?.main_entry);
      const hasTopLevelUuidBulkBlocks =
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        Object.keys(data).some((k) => {
          if (!BULK_BLOCK_UUID_KEY.test(k)) return false;
          const b = data[k];
          return b && bulkBlockHasRows(b);
        });

      // Move-copy-delete preview: multiple operations in one snapshot — View Original = first block's
      // originalContent; View New = last block's newContent (see bulk copy section + step payloads).
      if (
        isMoveCopyDeletePreview &&
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        hasTopLevelUuidBulkBlocks
      ) {
        const uuidKeys = Object.keys(data).filter(
          (k) =>
            BULK_BLOCK_UUID_KEY.test(k) &&
            data[k] &&
            bulkBlockHasRows(data[k]),
        );
        if (uuidKeys.length >= 2) {
          const firstBlock = data[uuidKeys[0]];
          const lastBlock = data[uuidKeys[uuidKeys.length - 1]];
          const vcRootPair = data.version_content;
          const vcAuditPair = mergeAuditVersionContent(auditTrailRow);
          const inferredPair = inferBulkMovedNodeType(
            vcAuditPair?.action_to_show ?? auditTrailRow?.action,
          );
          const treeNodeIdForView =
            viewType === "original"
              ? uuidKeys[0]
              : uuidKeys[uuidKeys.length - 1];
          let rows;
          if (viewType === "original") {
            const oc = Array.isArray(firstBlock?.originalContent)
              ? firstBlock.originalContent
              : [];
            rows =
              oc.length > 0
                ? oc
                : pickBulkBlockRowsForView(firstBlock, "original");
          } else {
            const nc = Array.isArray(lastBlock?.newContent)
              ? lastBlock.newContent
              : [];
            rows =
              nc.length > 0
                ? nc
                : pickBulkBlockRowsForView(lastBlock, "new");
          }
          if (rows.length > 0) {
            return {
              rows,
              meta: {
                ...(vcRootPair && typeof vcRootPair === "object"
                  ? vcRootPair
                  : {}),
                tree_node_id: treeNodeIdForView,
                type: inferredPair,
                operation: vcRootPair?.operation || "bulk_audit_trail",
                action_to_show: vcAuditPair?.action_to_show,
                move_copy_delete_pair: true,
              },
            };
          }
        }
      }

      // Section-by-section / revision snapshot envelope: { data: [...], partNumber, revision, status, ... }
      // Child moves (ATTACHMENT_MOVED, RICH_TEXT_MOVED, CANVAS/CANVA_MOVED, VIDEO_MOVED) often use this
      // shape (single_audit_trail); align meta with audit preview_tree_node_id when the snapshot root
      // omits tree_node_id or type.
      if (
        data &&
        typeof data === "object" &&
        Array.isArray(data.data)
      ) {
        const vcAuditEnvelope = mergeAuditVersionContent(auditTrailRow);
        const inferredEnvelopeType =
          inferBulkMovedNodeType(vcAuditEnvelope?.action_to_show) ||
          inferBulkMovedNodeType(data.action_to_show);
        const focusForDataEnvelope = getAuditFocusIdForBulkBlock(auditTrailRow);
        const rows = sortSectionsForSnapshotDisplay(data.data);
        return {
          rows,
          meta: {
            /** Align with fetchSnapshotContent.parseSnapshotJson — S3 envelopes often use part_number / rev */
            partNumber:
              data.partNumber ??
              data.part_number ??
              vcAuditEnvelope?.partNumber ??
              vcAuditEnvelope?.part_number,
            revision:
              data.revision ??
              data.rev ??
              data.part_rev ??
              vcAuditEnvelope?.revision ??
              vcAuditEnvelope?.rev ??
              vcAuditEnvelope?.part_rev,
            status: data.status ?? vcAuditEnvelope?.status,
            item_code_type:
              data.item_code_type ??
              vcAuditEnvelope?.item_code_type ??
              procedureCategoryLabelFromVc(data) ??
              procedureCategoryLabelFromVc(vcAuditEnvelope),
            pageTitle: data.pageTitle,
            breadcrumb_text: data.breadcrumb_text,
            tree_node_id:
              data.tree_node_id ||
              data.procedure_tree_id ||
              data.section_tree_node_id ||
              focusForDataEnvelope ||
              vcAuditEnvelope?.preview_tree_node_id ||
              vcAuditEnvelope?.tree_node_id,
            section_name: data.section_name,
            operation: data.operation,
            /** "section" | "step" | "substep" | "attachment" | … */
            type: data.type || inferredEnvelopeType,
          },
        };
      }

      // Handle different snapshot formats
      if (Array.isArray(data)) {
        // editrichtext.json format - direct array
        return sortSectionsForSnapshotDisplay(data);
      } else if (data.result && Array.isArray(data.result)) {
        // movestepsection.json format - nested in result
        return sortSectionsForSnapshotDisplay(data.result);
      } else if (
        !hasTopLevelUuidBulkBlocks &&
        data.originalContent != null &&
        data.newContent != null &&
        !(mainEntryEarly && hasTopLevelUuidBulkBlocks)
      ) {
        // Flat pair at root only when there are no UUID bulk blocks; otherwise UUID keys + stub root
        // would wrongly take this path (empty [] is truthy for originalContent).
        const content =
          viewType === "original" ? data.originalContent : data.newContent;
        return Array.isArray(content)
          ? sortSectionsForSnapshotDisplay(content)
          : content;
      } else if (data.auditData) {
        // Another possible format
        return data.auditData;
      } else {
        // main_entry: only when audit version_content has main_entry:true (ignore snapshot file's
        // data.version_content.main_entry so non-main entries still resolve by preview_tree_node_id).
        const vcAuditMain =
          mergeAuditTrailRowVersionContentDeep(auditTrailRow) ??
          mergeAuditVersionContent(auditTrailRow);
        const vcRootMainFlag = data.version_content;
        const mainEntryFromAudit = isTruthyMainEntry(vcAuditMain?.main_entry);

        // Non–main_entry: block UUID resolved by scanning new_content_snapshot for preview_tree_node_id
        // (see useEffect). Same key on previous + new — originalContent = View Original, newContent = View New.
        if (
          !mainEntryFromAudit &&
          options.resolvedBlockKey &&
          typeof data === "object" &&
          !Array.isArray(data)
        ) {
          const prefBlock = data[options.resolvedBlockKey];
          if (bulkBlockHasRows(prefBlock)) {
            const arrPref = pickBulkBlockRowsForView(prefBlock, viewType);
            const vcRootPref = data.version_content;
            const vcAuditPref = mergeAuditVersionContent(auditTrailRow);
            const inferredPref = inferBulkMovedNodeType(
              vcAuditPref?.action_to_show ?? auditTrailRow?.action,
            );
            const focusPref =
              getAuditFocusIdForBulkBlock(auditTrailRow) ||
              options.resolvedBlockKey;
            if (arrPref.length > 0) {
              return {
                rows: arrPref,
                meta: {
                  ...(vcRootPref && typeof vcRootPref === "object"
                    ? vcRootPref
                    : {}),
                  tree_node_id: focusPref,
                  type: inferredPref,
                  operation: vcRootPref?.operation || "bulk_audit_trail",
                  action_to_show: vcAuditPref?.action_to_show,
                },
              };
            }
          }
        }

        // main_entry (SECTION_COPIED / bulk copy / …): pick one block via findMainEntryBulkBlock;
        // pickBulkBlockRowsForView → originalContent (View Original) / newContent (View New).
        // Not restricted to move-preview route — same for /audit-trail/preview and embedded RevisionPreview.
        if (mainEntryFromAudit && typeof data === "object" && !Array.isArray(data)) {
          const pickedMain = findMainEntryBulkBlock(data, auditTrailRow);
          if (pickedMain) {
            const { key: mainBlockKey, block: firstBlock } = pickedMain;
            if (firstBlock && bulkBlockHasRows(firstBlock)) {
              const arrMain = pickBulkBlockRowsForView(firstBlock, viewType);
              const vcRootMain = data.version_content;
              const inferredMainType =
                inferBulkMovedNodeType(vcAuditMain?.action_to_show) ??
                inferBulkMovedNodeType(auditTrailRow?.action) ??
                inferBulkMovedNodeType(vcRootMainFlag?.action_to_show);
              const metaMain =
                vcRootMain &&
                typeof vcRootMain === "object" &&
                !Array.isArray(vcRootMain)
                  ? {
                      partNumber: vcRootMain.partNumber ?? vcRootMain.part_number,
                      revision: vcRootMain.revision ?? vcRootMain.rev,
                      status: vcRootMain.status,
                      item_code_type: vcRootMain.item_code_type,
                      operation: vcRootMain.operation,
                      action_to_show:
                        vcRootMain.action_to_show ?? vcAuditMain?.action_to_show,
                      tree_node_id: mainBlockKey,
                      main_entry: true,
                      ...(inferredMainType ? { type: inferredMainType } : {}),
                    }
                  : {
                      action_to_show: vcAuditMain?.action_to_show,
                      tree_node_id: mainBlockKey,
                      main_entry: true,
                      ...(inferredMainType ? { type: inferredMainType } : {}),
                    };
              if (arrMain.length > 0) {
                return {
                  rows: arrMain,
                  meta: metaMain,
                };
              }
            }
          }
        }

        // Without main_entry in audit version_content: use preview_tree_node_id (and fallbacks) to pick
        // ONE UUID block — search new_content_snapshot first for key; direct key data[id]; else first
        // block in file order whose trees contain the id. originalContent = View Original, newContent = View New.
        const vcAuditMove = mergeAuditVersionContent(auditTrailRow);
        if (
          !isTruthyMainEntry(vcAuditMove?.main_entry) &&
          typeof data === "object" &&
          !Array.isArray(data)
        ) {
          const focusMove = getAuditFocusIdForBulkBlock(auditTrailRow);
          if (focusMove) {
            const blockMove = findBulkAuditTrailBlock(data, focusMove);
            if (blockMove && bulkBlockHasRows(blockMove)) {
              const arrMove = pickBulkBlockRowsForView(blockMove, viewType);
              const vcRootMove = data.version_content;
              const inferredMove = inferBulkMovedNodeType(
                vcAuditMove?.action_to_show ?? auditTrailRow?.action,
              );
              if (arrMove.length > 0) {
                return {
                  rows: arrMove,
                  meta: {
                    ...(vcRootMove && typeof vcRootMove === "object"
                      ? vcRootMove
                      : {}),
                    tree_node_id: focusMove,
                    type: inferredMove,
                    operation:
                      vcRootMove?.operation || "bulk_audit_trail",
                    action_to_show: vcAuditMove?.action_to_show,
                  },
                };
              }
            }
          }
        }

        // bulk_audit_trail (non–move-preview routes, or move-preview fall-through): same block
        // resolution as move-preview branch above.
        const vcRoot = data?.version_content;
        const vcAudit = mergeAuditVersionContent(auditTrailRow);
        if (
          vcRoot &&
          vcRoot.operation === "bulk_audit_trail" &&
          vcAudit &&
          typeof data === "object" &&
          !Array.isArray(data)
        ) {
          const focusId =
            vcAudit.preview_tree_node_id ||
            vcAudit.tree_node_id ||
            vcAudit.section_tree_node_id ||
            vcAudit.step_tree_node_id ||
            vcAudit.sub_step_tree_node_id ||
            vcAudit.substep_tree_node_id ||
            vcAudit.canvas_tree_node_id ||
            vcAudit.canva_tree_node_id ||
            vcAudit.rich_text_node_id ||
            vcAudit.rich_text_tree_node_id ||
            vcAudit.attachment_tree_node_id ||
            vcAudit.video_tree_node_id;
          const block = focusId ? findBulkAuditTrailBlock(data, focusId) : null;
          const arr = block ? pickBulkBlockRowsForView(block, viewType) : [];
          if (Array.isArray(arr) && arr.length > 0) {
            const inferredType = inferBulkMovedNodeType(vcAudit.action_to_show);
            return {
              rows: arr,
              meta: {
                ...vcRoot,
                tree_node_id: focusId,
                type: inferredType,
                operation: vcRoot.operation || "bulk_audit_trail",
                action_to_show: vcAudit.action_to_show,
              },
            };
          }
        }

        // Handle case where data is an object with UUID keys (like response.json - multiple operations)
        // Merge ALL keys so we show the full procedure tree (all sections, steps, substeps, and child nodes)
        const keys = Object.keys(data);
        const getSectionId = (s) =>
          s?.tree_node_id || s?.detail?.section_tree_node_id || s?.section_id_ref;
        const getStepId = (st) =>
          st?.tree_node_id || st?.step_id_ref || st?.detail?.step_tree_node_id;
        const getSubstepId = (ss) =>
          ss?.tree_node_id || ss?.substep_id_ref || ss?.detail?.sub_step_tree_node_id;
        const getChildId = (c) =>
          c?.tree_node_id || c?.detail?.element_id || c?.detail?.canvas_tree_node_id ||
          c?.detail?.rich_text_node_id || c?.detail?.video_tree_node_id || c?.detail?.attachment_tree_node_id;

        const mergeSubstep = (existing, incoming) => {
          if (!existing) return incoming;
          const childrenMap = new Map();
          (existing.children || []).forEach((ch) => {
            const id = getChildId(ch);
            if (id) childrenMap.set(id, ch);
          });
          (incoming.children || []).forEach((ch) => {
            const id = getChildId(ch);
            if (id) childrenMap.set(id, ch);
          });
          return { ...incoming, children: Array.from(childrenMap.values()) };
        };

        const mergeStep = (existing, incoming) => {
          if (!existing) return incoming;
          const childrenMap = new Map();
          (existing.children || []).forEach((ch) => {
            const id = getChildId(ch);
            if (id) childrenMap.set(id, ch);
          });
          (incoming.children || []).forEach((ch) => {
            const id = getChildId(ch);
            if (id) childrenMap.set(id, ch);
          });
          const substepsMap = new Map();
          (existing.substeps || []).forEach((ss) => {
            const id = getSubstepId(ss);
            if (id) substepsMap.set(id, ss);
          });
          (incoming.substeps || []).forEach((ss) => {
            const id = getSubstepId(ss);
            substepsMap.set(id, mergeSubstep(substepsMap.get(id), ss));
          });
          return {
            ...incoming,
            children: Array.from(childrenMap.values()),
            substeps: Array.from(substepsMap.values()),
          };
        };

        const sectionsMap = new Map();
        for (const key of keys) {
          if (key === "version_content" || key === "auditData") continue;
          const content = data[key];
          if (!content || !bulkBlockHasRows(content)) continue;
          const arr = pickBulkBlockRowsForView(content, viewType);
          if (!Array.isArray(arr) || arr.length === 0) continue;
          for (const section of arr) {
            const sid = getSectionId(section);
            if (!sid) continue;
            let sec = sectionsMap.get(sid);
            if (!sec) {
              sec = { ...section, steps: [] };
              sectionsMap.set(sid, sec);
            }
            const stepsMap = new Map();
            (sec.steps || []).forEach((st) => {
              const stepId = getStepId(st);
              if (stepId) stepsMap.set(stepId, st);
            });
            (section.steps || []).forEach((st) => {
              const stepId = getStepId(st);
              if (stepId) stepsMap.set(stepId, mergeStep(stepsMap.get(stepId), st));
            });
            sec.steps = Array.from(stepsMap.values()).sort(
              (a, b) =>
                parseInt(a?.sibling_order ?? a?.detail?.sibling_order ?? "0", 10) -
                parseInt(b?.sibling_order ?? b?.detail?.sibling_order ?? "0", 10),
            );
          }
        }

        const mergedSections = Array.from(sectionsMap.values()).sort(
          (a, b) =>
            parseInt(a?.sibling_order ?? a?.detail?.sibling_order ?? "0", 10) -
            parseInt(b?.sibling_order ?? b?.detail?.sibling_order ?? "0", 10),
        );
        return mergedSections;
      }
    } catch (error) {
      console.error("RevisionPreview Error fetching snapshot data:", error);
      throw error;
    }
  };

  useEffect(() => {
    (async () => {
      // Use external audit trail data if provided, otherwise fetch it
      if (externalAuditTrailData) {
        setAuditData(externalAuditTrailData);
        // Initialize selected nodes when data is loaded
        initializeSelectedNodes(externalAuditTrailData);
      } else {
        const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }));
        const data = resp.payload?.results ?? resp.payload;
        if (data) {
          setAuditData(data);
          initializeSelectedNodes(data);
        }
      }
    })();
  }, [dispatch, audit_trail_id, externalAuditTrailData]);

  // Build minimal section/step/child tree from version_content for move/copy when no snapshot or inline content
  const buildMinimalContentFromVc = (nodeVc) => {
    if (!nodeVc || (!nodeVc.section_tree_node_id && !nodeVc.section_id_ref)) return [];
    const sectionId = nodeVc.section_tree_node_id || nodeVc.section_id_ref;
    const stepId = nodeVc.step_tree_node_id || nodeVc.step_id_ref;
    const childNode = {
      ...nodeVc,
      type: nodeVc.type === "canva" ? "canvas" : nodeVc.type,
      version_details: { version_content: nodeVc },
      detail: nodeVc,
      canvas_tree_node_id: nodeVc.preview_tree_node_id || nodeVc.canvas_tree_node_id || nodeVc.canva_tree_node_id,
      canva_tree_node_id: nodeVc.preview_tree_node_id || nodeVc.canvas_tree_node_id || nodeVc.canva_tree_node_id,
    };
    const step = {
      step_tree_node_id: stepId,
      step_id_ref: stepId,
      version_id_ref: stepId,
      steps: undefined,
      substeps: [],
      children: [childNode],
    };
    const section = {
      section_tree_node_id: sectionId,
      section_id_ref: sectionId,
      version_id_ref: sectionId,
      steps: [step],
    };
    return [section];
  };

  // Fetch snapshot data: from S3 URLs when present, else from inline originingalContent/newContent (e.g. SUBSTEP_NODE_DELETED), else minimal tree for move/copy (e.g. CANVAS_MOVED with no URLs)
  useEffect(() => {
    (async () => {
      const vc = audit_trail_data?.version_content || {};
      const hasSnapshotUrls =
        audit_trail_data?.previous_content_snapshot || audit_trail_data?.new_content_snapshot;
      const inlineOriginal = vc.originingalContent ?? vc.originalContent;
      const inlineNew = vc.newContent;
      const hasInlineContent =
        Array.isArray(inlineOriginal) && Array.isArray(inlineNew);

      if (hasInlineContent && !hasSnapshotUrls) {
        // For RICH-TEXT copy, inline newContent children may be ref-only; merge version_content into the copied node so it has rich_text_content
        let finalNew = inlineNew;
        const isRichTextCopy =
          (vc.type === "rich-text" || vc.type === "rich_text") &&
          ((vc.action_to_show || "").toUpperCase().includes("RICH") && (vc.action_to_show || "").toUpperCase().includes("COPIED"));
        if (isRichTextCopy && vc.rich_text_content && Array.isArray(inlineNew)) {
          let mergedOnce = false;
          const mergeVcIntoFirstRefOnlyChild = (ch) => {
            const isRefOnly = ch && (Object.keys(ch).length <= 2 && ch.version_id_ref || !ch.rich_text_content && !ch.version_details && !ch.detail);
            if (isRefOnly && !mergedOnce && vc.rich_text_content) {
              mergedOnce = true;
              return { ...ch, type: "rich-text", version_details: { version_content: vc }, detail: vc, rich_text_content: vc.rich_text_content };
            }
            return ch;
          };
          finalNew = inlineNew.map((sec) => ({
            ...sec,
            steps: (sec.steps || []).map((st) => ({
              ...st,
              children: (st.children || []).map(mergeVcIntoFirstRefOnlyChild),
              substeps: (st.substeps || []).map((ss) => ({
                ...ss,
                children: (ss.children || []).map(mergeVcIntoFirstRefOnlyChild),
              })),
            })),
          }));
        }
        setOriginalContent({
          data: inlineOriginal,
          loaded: true,
          meta: null,
        });
        setNewContent({ data: finalNew, loaded: true, meta: null });
        return;
      }

      if (!hasSnapshotUrls) {
        const action = (vc.action_to_show || "").toUpperCase();
        const isMoveOrCopy =
          action.includes("_MOVED") || action.includes("_COPIED");
        if (isMoveOrCopy && (vc.section_tree_node_id || vc.section_id_ref)) {
          const parentVc = audit_trail_data?.parent_version_data?.version_content;
          const originalMinimal = audit_trail_data?.previous_content_snapshot
            ? buildMinimalContentFromVc(parentVc || vc)
            : [];
          const newMinimal = audit_trail_data?.new_content_snapshot
            ? buildMinimalContentFromVc(vc)
            : [];
          setOriginalContent({
            data: originalMinimal,
            loaded: true,
            meta: null,
          });
          setNewContent({ data: newMinimal, loaded: true, meta: null });
        }
        return;
      }
      const vcMergedForBlock =
        mergeAuditTrailRowVersionContentDeep(audit_trail_data);
      const mainEntryAuditForBlock = isTruthyMainEntry(vcMergedForBlock?.main_entry);
      /**
       * Non–main_entry: resolve focus id with preview_tree_node_id first (see getAuditFocusIdForBulkBlock;
       * mergeAuditTrailRowVersionContentDeep includes parent_version_data). Load new_content_snapshot once
       * and find the first bulk block whose trees contain that id → resolvedBlockKey. Reuse that key on
       * previous_content_snapshot. Per fetch: originalContent → View Original, newContent → View New.
       */
      const focusForBulkBlock = getAuditFocusIdForBulkBlock(audit_trail_data);

      let resolvedBlockKey = null;
      let newSnapshotPrefetched = null;
      if (
        !mainEntryAuditForBlock &&
        focusForBulkBlock &&
        audit_trail_data.new_content_snapshot
      ) {
        try {
          const keyResp = await fetch(
            audit_trail_data.new_content_snapshot,
            SNAPSHOT_FETCH_INIT,
          );
          if (keyResp.ok) {
            newSnapshotPrefetched = await keyResp.json();
            resolvedBlockKey = findBulkAuditTrailBlockKey(
              newSnapshotPrefetched,
              focusForBulkBlock,
            );
          }
        } catch (e) {
        }
      }

      const fetchAndSet = async (url, viewType, setter, extra = {}) => {
        if (!url && extra.prefetchedJson == null) return;
        try {
          const snapshotData = await fetchSnapshotData(
            url,
            viewType,
            audit_trail_data,
            {
              resolvedBlockKey,
              prefetchedJson: extra.prefetchedJson,
            },
          );
          let rows = [];
          let meta = null;
          if (
            snapshotData &&
            typeof snapshotData === "object" &&
            Array.isArray(snapshotData.rows)
          ) {
            rows = snapshotData.rows;
            meta = snapshotData.meta || null;
          } else if (Array.isArray(snapshotData)) {
            rows = snapshotData;
          }
          setter({ data: rows, loaded: true, meta });
        } catch (error) {
          console.error(`Error fetching ${viewType} snapshot:`, error);
          setter({
            data: [],
            loaded: true,
            meta: null,
            error: error.message,
          });
        }
      };

      if (!audit_trail_data.previous_content_snapshot) {
        setOriginalContent((prev) =>
          prev.loaded ? prev : { data: [], loaded: true, meta: null },
        );
      }
      if (!audit_trail_data.new_content_snapshot) {
        setNewContent((prev) =>
          prev.loaded ? prev : { data: [], loaded: true, meta: null },
        );
      }

      const fetchTasks = [];
      if (audit_trail_data.previous_content_snapshot && !originalContent.loaded) {
        fetchTasks.push(
          fetchAndSet(
            audit_trail_data.previous_content_snapshot,
            "original",
            setOriginalContent,
            {},
          ),
        );
      }
      if (audit_trail_data.new_content_snapshot && !newContent.loaded) {
        fetchTasks.push(
          fetchAndSet(
            audit_trail_data.new_content_snapshot,
            "new",
            setNewContent,
            { prefetchedJson: newSnapshotPrefetched },
          ),
        );
      }
      await Promise.all(fetchTasks);
    })();
  }, [audit_trail_data]);

  // Initialize selected nodes - simplified for Step 2 filtering
  const initializeSelectedNodes = (data) => {
    const selected = {};
    const action = normalizeActionTokens(data.version_content?.action_to_show);
    const vc = data.version_content || {};
    const pvc = data.parent_version_data?.version_content || {};

    // Section move/copy: preview_tree_node_id is the moved section's procedure tree id
    if (action?.includes("SECTION_MOVED") || action?.includes("SECTION_COPIED")) {
      const sectionId =
        vc.preview_tree_node_id ||
        vc.section_tree_node_id ||
        vc.tree_node_id ||
        pvc.preview_tree_node_id ||
        pvc.section_tree_node_id;
      if (sectionId) selected[sectionId] = true;
    }

    // Substep move/copy
    if (action?.includes("SUBSTEP_MOVED") || action?.includes("SUBSTEP_COPIED")) {
      const ssid =
        vc.preview_tree_node_id ||
        vc.sub_step_tree_node_id ||
        vc.substep_tree_node_id ||
        vc.substep_id_ref ||
        pvc.preview_tree_node_id ||
        pvc.sub_step_tree_node_id;
      if (ssid) selected[ssid] = true;
    }

    // For move/copy actions, select the target step so checkbox appears checked
    // Use both pageTitle and step_tree_node_id so filterContentBySelection and Step component work
    if (action?.includes("STEP_MOVED") || action?.includes("STEP_COPIED")) {
      const targetPageTitle = vc.pageTitle || pvc.pageTitle;
      const targetStepId =
        vc.preview_tree_node_id ||
        vc.step_tree_node_id ||
        pvc.preview_tree_node_id ||
        pvc.step_tree_node_id ||
        (data.node_indexing && Object.keys(data.node_indexing)[0]);

      if (targetPageTitle) selected[targetPageTitle] = true;
      if (targetStepId) selected[targetStepId] = true;
    }

    // For node move actions (video/rich-text/canvas/attachment), select the target node id
    // and parent section/step so the hierarchy is visible and checkboxes reflect context
    if (
      action?.includes("VIDEO_") ||
      action?.includes("RICH_TEXT_") ||
      action?.includes("RICH-TEXT_") ||
      action?.includes("CANVAS_") ||
      action?.includes("CANVA_") ||
      action?.includes("ATTACHMENT_")
    ) {
      /** Merge parent + current VC — parent alone often lacks ids (e.g. CREATE_*), while ATTACHMENT_MOVED has preview_tree_node_id */
      const nodeSource = {
        ...(data.parent_version_data?.version_content || {}),
        ...(data.version_content || {}),
      };
      /** Procedure ids first (match audit preview_tree_node_id / child.tree_node_id); entity *_tree_node_id refs last */
      const targetNodeId =
        nodeSource?.preview_tree_node_id ||
        nodeSource?.tree_node_id ||
        nodeSource?.element_id ||
        nodeSource?.canvas_tree_node_id ||
        nodeSource?.canva_tree_node_id ||
        nodeSource?.rich_text_node_id ||
        nodeSource?.rich_text_tree_node_id ||
        nodeSource?.video_tree_node_id ||
        nodeSource?.attachment_tree_node_id;

      if (targetNodeId) {
        selected[targetNodeId] = true;
      }
      /* Parent step/section: collectResolvedTreeIdsFromSnapshot adds procedure step id; do not seed entity step_id_ref
       * (shared by duplicate/copied steps — would check sibling rows). */
    }

    // Section / step / substep create or update (snapshot-backed): seed selection with API ids; snapshot load may add matching tree_node_id
    const actionText = `${data.action || ""} ${action || ""}`;
    const actionUpper = actionText.toUpperCase();
    const isSectionSave =
      actionUpper.includes("CREATE_SECTION") ||
      actionUpper.includes("UPDATE_SECTION") ||
      (/SAVE\s+SECTION/i.test(actionText) &&
        !actionUpper.includes("STEP") &&
        !actionUpper.includes("SUBSTEP")) ||
      (/EDIT\s+SECTION/i.test(actionText) && !actionUpper.includes("SUBSTEP"));
    const isStepSave =
      (actionUpper.includes("STEP") && !actionUpper.includes("SUBSTEP")) ||
      actionUpper.includes("CREATE_STEP") ||
      actionUpper.includes("UPDATE_STEP") ||
      (/SAVE\s+STEP/i.test(actionText) && !actionUpper.includes("SUBSTEP")) ||
      (/EDIT\s+STEP/i.test(actionText) && !actionUpper.includes("SUBSTEP"));
    const isSubstepSave =
      actionUpper.includes("SUBSTEP") ||
      actionUpper.includes("CREATE_SUBSTEP") ||
      actionUpper.includes("UPDATE_SUBSTEP") ||
      /SAVE\s+SUBSTEP/i.test(actionText) ||
      /EDIT\s+SUBSTEP/i.test(actionText);
    const isAttachmentSave =
      /SAVE\s+ATTACHMENT|EDIT\s+ATTACHMENT/i.test(actionText) ||
      actionUpper.includes("CREATE_ATTACHMENT") ||
      actionUpper.includes("UPDATE_ATTACHMENT");
    const isCanvasSave =
      /SAVE\s+CANVAS|EDIT\s+CANVAS/i.test(actionText) ||
      actionUpper.includes("CREATE_CANVAS") ||
      actionUpper.includes("UPDATE_CANVAS");
    const isRichTextSave =
      /SAVE\s+RICH\s+TEXT|EDIT\s+RICH\s+TEXT/i.test(actionText) ||
      actionUpper.includes("CREATE_RICH_TEXT") ||
      actionUpper.includes("UPDATE_RICH_TEXT");
    const isVideoSave =
      /SAVE\s+VIDEO|EDIT\s+VIDEO/i.test(actionText) ||
      actionUpper.includes("CREATE_VIDEO") ||
      actionUpper.includes("UPDATE_VIDEO");

    if (isSectionSave) {
      const sid =
        vc.tree_node_id || vc.section_tree_node_id || vc.section_id_ref;
      if (sid) selected[sid] = true;
    } else if (isStepSave) {
      const stid =
        vc.step_tree_node_id || vc.step_id_ref || vc.tree_node_id;
      if (stid) selected[stid] = true;
    } else if (isSubstepSave) {
      // UPDATE_SUBSTEP often omits ids on version_content; parent (e.g. CREATE_SUBSTEP) has sub_step_tree_node_id
      const ssid =
        vc.sub_step_tree_node_id ||
        vc.substep_tree_node_id ||
        vc.substep_id_ref ||
        vc.tree_node_id ||
        pvc.sub_step_tree_node_id ||
        pvc.substep_tree_node_id ||
        pvc.substep_id_ref;
      if (ssid) selected[ssid] = true;
    } else if (isAttachmentSave) {
      const aid =
        vc.attachment_tree_node_id ||
        vc.preview_tree_node_id ||
        vc.tree_node_id ||
        pvc.attachment_tree_node_id ||
        pvc.preview_tree_node_id ||
        pvc.tree_node_id;
      if (aid) selected[aid] = true;
    } else if (isCanvasSave) {
      /**
       * Prefer leaf / preview ids before generic tree_node_id — audit tree_node_id is often the parent
       * step; selecting it marks the whole step checked and every child shows (wrong canvas path).
       */
      const cid =
        vc.preview_tree_node_id ||
        vc.canvas_tree_node_id ||
        vc.canva_tree_node_id ||
        vc.tree_node_id ||
        vc.procedure_tree_ref ||
        pvc.preview_tree_node_id ||
        pvc.canvas_tree_node_id ||
        pvc.canva_tree_node_id ||
        pvc.tree_node_id ||
        pvc.procedure_tree_ref;
      if (cid) selected[cid] = true;
    } else if (isRichTextSave) {
      const rid =
        vc.tree_node_id ||
        vc.procedure_tree_ref ||
        vc.rich_text_node_id ||
        vc.rich_text_tree_node_id ||
        vc.preview_tree_node_id ||
        pvc.tree_node_id ||
        pvc.procedure_tree_ref ||
        pvc.rich_text_node_id ||
        pvc.rich_text_tree_node_id ||
        pvc.preview_tree_node_id;
      if (rid) selected[rid] = true;
    } else if (isVideoSave) {
      const vid =
        vc.tree_node_id ||
        vc.procedure_tree_ref ||
        vc.video_tree_node_id ||
        vc.preview_tree_node_id ||
        pvc.tree_node_id ||
        pvc.procedure_tree_ref ||
        pvc.video_tree_node_id ||
        pvc.preview_tree_node_id;
      if (vid) selected[vid] = true;
    }

    setSelectedNodes(selected);
  };

  // Match audit version_content ids to snapshot tree_node_id so filtering shows only the saved node branch
  useEffect(() => {
    const vc = audit_trail_data?.version_content || {};
    const pvc = audit_trail_data?.parent_version_data?.version_content || {};
    const targetIds = [
      ...collectAuditTargetTreeIds(vc),
      ...collectAuditTargetTreeIds(pvc),
    ];
    if (newContent.meta?.tree_node_id) {
      targetIds.push(newContent.meta.tree_node_id);
    }
    if (originalContent.meta?.tree_node_id) {
      targetIds.push(originalContent.meta.tree_node_id);
    }
    if (targetIds.length === 0) return;
    const fromNew =
      newContent.loaded && Array.isArray(newContent.data)
        ? collectResolvedTreeIdsFromSnapshot(newContent.data, targetIds)
        : [];
    const fromOrig =
      originalContent.loaded && Array.isArray(originalContent.data)
        ? collectResolvedTreeIdsFromSnapshot(originalContent.data, targetIds)
        : [];
    const merged = [
      ...new Set([
        ...fromNew,
        ...fromOrig,
        ...(newContent.meta?.tree_node_id ? [newContent.meta.tree_node_id] : []),
        ...(originalContent.meta?.tree_node_id
          ? [originalContent.meta.tree_node_id]
          : []),
      ]),
    ];
    if (merged.length === 0) return;
    setSelectedNodes((prev) => {
      const next = { ...prev };
      merged.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
  }, [
    newContent.loaded,
    newContent.data,
    newContent.meta,
    originalContent.loaded,
    originalContent.data,
    originalContent.meta,
    audit_trail_data?.version_content,
    audit_trail_data?.parent_version_data?.version_content,
    audit_trail_id,
  ]);

  // Handle node checkbox toggle
  const handleNodeToggle = (nodeId) => {
    setSelectedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  // Helpers for ID resolution - support both API (version_details.version_content) and snapshot (detail) formats
  const getSectionIdForFilter = (s) =>
    s?.tree_node_id ||
    s?.version_details?.version_content?.section_tree_node_id ||
    s?.detail?.section_tree_node_id ||
    s?.version_details?.version_content?.section_id_ref ||
    s?.section_tree_node_id ||
    s?.section_id_ref;
  const getStepIdForFilter = (st) =>
    st?.tree_node_id ||
    st?.version_details?.version_content?.step_tree_node_id ||
    st?.detail?.step_tree_node_id ||
    st?.step_tree_node_id ||
    st?.step_id_ref;
  const getSubstepIdForFilter = (ss) =>
    ss?.tree_node_id ||
    ss?.version_details?.version_content?.sub_step_tree_node_id ||
    ss?.detail?.sub_step_tree_node_id ||
    ss?.sub_step_tree_node_id ||
    ss?.substep_id_ref;
  // Prefer procedure tree_node_id (matches snapshot meta / audit trail) over detail.*_ref ids
  const getChildIdForFilter = (c) =>
    c?.tree_node_id ||
    c?.version_details?.version_content?.element_id ||
    c?.version_details?.version_content?.canvas_tree_node_id ||
    c?.version_details?.version_content?.canva_tree_node_id ||
    c?.version_details?.version_content?.rich_text_node_id ||
    c?.version_details?.version_content?.rich_text_tree_node_id ||
    c?.version_details?.version_content?.video_tree_node_id ||
    c?.version_details?.version_content?.attachment_tree_node_id ||
    c?.version_details?.version_content?.preview_tree_node_id ||
    c?.detail?.element_id ||
    c?.detail?.canvas_tree_node_id ||
    c?.detail?.canva_tree_node_id ||
    c?.detail?.rich_text_node_id ||
    c?.detail?.rich_text_tree_node_id ||
    c?.detail?.video_tree_node_id ||
    c?.detail?.attachment_tree_node_id ||
    c?.detail?.preview_tree_node_id;

  /**
   * Audit may key selection by entity ref (e.g. detail.canvas_tree_node_id "cnv_ref_…") while
   * getChildIdForFilter prefers procedure tree_node_id — substeps were dropped and under-substep
   * canvas never matched. Match if any stable id on the child is selected.
   */
  const collectChildIdsForSelection = (c) => {
    const d = c?.detail || {};
    const vc = c?.version_details?.version_content || {};
    const ids = [
      getChildIdForFilter(c),
      c?.tree_node_id,
      d.procedure_tree_ref,
      vc.procedure_tree_ref,
      d.element_id,
      vc.element_id,
      d.canvas_tree_node_id,
      vc.canvas_tree_node_id,
      d.canva_tree_node_id,
      vc.canva_tree_node_id,
      d.rich_text_node_id,
      d.rich_text_tree_node_id,
      vc.rich_text_node_id,
      vc.rich_text_tree_node_id,
      d.video_tree_node_id,
      vc.video_tree_node_id,
      d.attachment_tree_node_id,
      vc.attachment_tree_node_id,
      d.preview_tree_node_id,
      vc.preview_tree_node_id,
    ].filter(Boolean);
    return [...new Set(ids)];
  };

  const childMatchesSelectedNodes = (c) =>
    collectChildIdsForSelection(c).some((id) => selectedNodes[id]);

  const getStepPageTitle = (st) =>
    st?.version_details?.version_content?.pageTitle ||
    st?.detail?.pageTitle ||
    st?.detail?.step_title;

  // Helper to check if a step has any selected child (direct or in substeps)
  const stepHasSelectedChild = (step) => {
    const stepId = getStepIdForFilter(step);
    const stepPageTitle = getStepPageTitle(step);
    const isStepSelected = selectedNodes[stepPageTitle] || selectedNodes[stepId];
    if (isStepSelected) return true;
    if (step.children?.some((c) => childMatchesSelectedNodes(c))) return true;
    if (
      step.substeps?.some((ss) => selectedNodes[getSubstepIdForFilter(ss)])
    ) {
      return true;
    }
    return step.substeps?.some((ss) =>
      ss.children?.some((c) => childMatchesSelectedNodes(c)),
    );
  };

  // Filter content based on selected nodes
  const filterContentBySelection = (data) => {
    if (!data || !Array.isArray(data)) return data;

    // If no nodes are selected yet, show all (don't filter)
    if (Object.keys(selectedNodes).length === 0) return data;

    return data
      .map((section) => {
        const sectionId = getSectionIdForFilter(section);

        // More flexible section filtering - check if section contains the step or selected child we want
        const shouldShowSection =
          selectedNodes[sectionId] ||
          section.steps?.some((step) => {
            const stepId = getStepIdForFilter(step);
            return selectedNodes[stepId] || stepHasSelectedChild(step);
          });

        if (!shouldShowSection) {
          return null;
        }

        const filteredSteps = section.steps
          ?.map((step) => {
            const stepId = getStepIdForFilter(step);
            const stepPageTitle = getStepPageTitle(step);

            // Include step if: selected, or section selected, or contains a selected child
            const shouldShowStep =
              selectedNodes[stepId] ||
              selectedNodes[sectionId] ||
              selectedNodes[stepPageTitle] ||
              stepHasSelectedChild(step);

            if (!shouldShowStep) {
              return null;
            }

            // Always include children and substeps of selected steps
            const filteredChildren =
              step.children?.filter((child) => {
                const childId = getChildIdForFilter(child);
                const isStepSelected =
                  selectedNodes[stepPageTitle] || selectedNodes[stepId];
                return (
                  !childId ||
                  childMatchesSelectedNodes(child) ||
                  isStepSelected
                );
              }) || step.children; // Fallback to original children if filtering fails

            const filteredSubsteps = step.substeps
              ?.map((substep) => {
                const substepId = getSubstepIdForFilter(substep);

                // Always include substeps of selected steps
                const isStepSelected =
                  selectedNodes[stepPageTitle] || selectedNodes[stepId];
                // Include substep if it contains a selected child (for child node moves)
                const substepHasSelectedChild = substep.children?.some((c) =>
                  childMatchesSelectedNodes(c),
                );

                if (isStepSelected || substepHasSelectedChild) {
                  const filteredSubstepChildren =
                    substep.children?.filter((child) => {
                      const childId = getChildIdForFilter(child);
                      return (
                        !childId ||
                        childMatchesSelectedNodes(child) ||
                        isStepSelected
                      );
                    }) || substep.children;

                  return {
                    ...substep,
                    children: filteredSubstepChildren,
                  };
                }

                // If substep is not selected and parent step is not selected and no selected child, skip it
                if (substepId && !selectedNodes[substepId]) {
                  return null;
                }

                const filteredSubstepChildren =
                  substep.children?.filter((child) => {
                    const childId = getChildIdForFilter(child);
                    return (
                      !childId ||
                      childMatchesSelectedNodes(child) ||
                      isStepSelected
                    );
                  }) || substep.children;

                return {
                  ...substep,
                  children: filteredSubstepChildren,
                };
              })
              .filter(Boolean);

            return {
              ...step,
              children: filteredChildren,
              substeps: filteredSubsteps,
            };
          })
          .filter(Boolean);

        return {
          ...section,
          steps: filteredSteps,
        };
      })
      .filter(Boolean);
  };

  // Process content data based on selected view
  const rawContentData =
    selectedView === "new" ? newContent?.data : originalContent?.data;
  const isContentLoaded =
    selectedView === "new" ? newContent.loaded : originalContent.loaded;

  // Process content data
  const processedData = processContentData(rawContentData);

  // Use external data when provided so header shows correct values on first paint (no wait for setState)
  const dataForHeader = externalAuditTrailData || audit_trail_data;
  /** Parent + child version_content so procedure type (SUBASSEMBLY) survives when the child row omits it */
  const vcMergedForHeader = mergeAuditTrailRowVersionContentDeep(dataForHeader);

  // Apply filtering based on selected nodes - simplified approach
  const baseTitleSource =
    dataForHeader.parent_version_data?.version_content ||
    dataForHeader.version_content?.version_content ||
    dataForHeader.version_content;

  // Extract section information from action_to_show for better title resolution
  // Check multiple sources for action_to_show
  const actionShow = 
    dataForHeader.version_content?.action_to_show || 
    dataForHeader.action || 
    newContent.meta?.action_to_show ||
    (selectedView === "new" && newContent.loaded && newContent.data?.length > 0 && newContent.data[0]?.action_to_show) ||
    '';
  
  const sectionNumMatch = /section\s+(\d+)/i.exec(actionShow);
  const inferredSectionTitle = sectionNumMatch ? `Section ${sectionNumMatch[1]}` : null;

  // Also try to extract section info from breadcrumb text
  const breadcrumbText = 
    dataForHeader.version_content?.breadcrumb_text || 
    baseTitleSource?.breadcrumb_text ||
    newContent.meta?.breadcrumb_text ||
    (selectedView === "new" && newContent.loaded && newContent.data?.length > 0 && newContent.data[0]?.breadcrumb_text);
  const breadcrumbSectionMatch = breadcrumbText && typeof breadcrumbText === 'string' 
    ? /Section\s+(\d+)/i.exec(breadcrumbText.split(' > ').pop())
    : null;
  const breadcrumbSectionTitle = breadcrumbSectionMatch ? `Section ${breadcrumbSectionMatch[1]}` : null;

  // Enhanced titleSource that includes section information
  const titleSource = {
    ...baseTitleSource,
    pageTitle: baseTitleSource?.pageTitle || inferredSectionTitle || breadcrumbSectionTitle,
  };

  // Snapshot JSON (S3) may include partNumber, revision, status at root — prefer current view's snapshot meta
  const activeSnapshotMeta =
    selectedView === "new" ? newContent.meta : originalContent.meta;

  // Single source for status/revision used by ProcedureStatusInfo (handles section/create flows where API uses version_content.revision not .rev)
  const displayStatus =
    dataForHeader.parent_version_data?.version_content?.status ??
    dataForHeader.version_content?.version_content?.status ??
    dataForHeader.version_content?.status ??
    dataForHeader.version_content?.status_changed_to ??
    dataForHeader.procedure_revision?.status ??
    activeSnapshotMeta?.status;
  const displayRevision =
    dataForHeader.parent_version_data?.version_content?.rev ??
    dataForHeader.parent_version_data?.version_content?.revision ??
    dataForHeader.version_content?.version_content?.rev ??
    dataForHeader.version_content?.version_content?.revision ??
    dataForHeader.version_content?.rev ??
    dataForHeader.version_content?.revision ??
    dataForHeader.version_content?.part_rev ??
    dataForHeader.procedure_revision?.revision ??
    dataForHeader.procedure_revision?.rev ??
    dataForHeader.rev ??
    dataForHeader.revision ??
    activeSnapshotMeta?.revision ??
    activeSnapshotMeta?.rev ??
    activeSnapshotMeta?.part_rev;

  const targetPageTitle = titleSource?.pageTitle;
  const rawContentType =
    titleSource?.type ||
    activeSnapshotMeta?.type ||
    audit_trail_data?.version_content?.type;
  /** RICH-TEXT_MOVED / CANVAS_MOVED often omit type on VC; infer from action_to_show so child-node UI matches */
  const auditActionForChildType =
    audit_trail_data?.version_content?.action_to_show ||
    audit_trail_data?.parent_version_data?.version_content?.action_to_show ||
    "";
  const inferredNodeTypeFromAuditAction =
    inferBulkMovedNodeType(auditActionForChildType);
  const normalizedContentType =
    rawContentType === "canva"
      ? "canvas"
      : rawContentType === "rich_text"
        ? "rich-text"
        : rawContentType ||
          (inferredNodeTypeFromAuditAction &&
          ["rich-text", "video", "canvas", "attachment"].includes(
            inferredNodeTypeFromAuditAction,
          )
            ? inferredNodeTypeFromAuditAction
            : undefined);

  const targetNodeId =
    titleSource?.element_id ||
    titleSource?.canvas_tree_node_id ||
    titleSource?.canva_tree_node_id ||
    titleSource?.rich_text_node_id ||
    titleSource?.rich_text_tree_node_id ||
    titleSource?.video_tree_node_id ||
    titleSource?.attachment_tree_node_id ||
    titleSource?.preview_tree_node_id ||
    audit_trail_data?.version_content?.preview_tree_node_id ||
    audit_trail_data?.version_content?.tree_node_id;

  const fallbackNodeId =
    audit_trail_data.parent_version_data?.version_id ||
    audit_trail_data.version_id ||
    audit_trail_data.parent_version;
  const effectiveTargetNodeId = targetNodeId || fallbackNodeId;

  const filterToTargetNode = (data) => {
    if (!targetNodeId) return [];
    if (!Array.isArray(data)) return [];

    return data
      .map((section) => {
        const filteredSteps =
          section.steps
            ?.map((step) => {
              const filteredChildren =
                step.children?.filter((child) => {
                  const c = child?.version_details?.version_content || child?.detail;
                  const childId =
                    child?.tree_node_id ||
                    c?.element_id ||
                    c?.canvas_tree_node_id ||
                    c?.canva_tree_node_id ||
                    c?.rich_text_node_id ||
                    c?.rich_text_tree_node_id ||
                    c?.video_tree_node_id ||
                    c?.attachment_tree_node_id ||
                    c?.preview_tree_node_id ||
                    c?.procedure_tree_ref;
                  return childId === targetNodeId;
                }) || [];

              const filteredSubsteps =
                step.substeps
                  ?.map((substep) => {
                    const filteredSubChildren =
                      substep.children?.filter((child) => {
                        const c = child?.version_details?.version_content || child?.detail;
                        const childId =
                          child?.tree_node_id ||
                          c?.element_id ||
                          c?.canvas_tree_node_id ||
                          c?.canva_tree_node_id ||
                          c?.rich_text_node_id ||
                          c?.rich_text_tree_node_id ||
                          c?.video_tree_node_id ||
                          c?.attachment_tree_node_id ||
                          c?.preview_tree_node_id ||
                          c?.procedure_tree_ref;
                        return childId === targetNodeId;
                      }) || [];

                    if (filteredSubChildren.length === 0) return null;
                    return {
                      ...substep,
                      children: filteredSubChildren,
                    };
                  })
                  .filter(Boolean) || [];

              if (
                filteredChildren.length === 0 &&
                filteredSubsteps.length === 0
              )
                return null;

              return {
                ...step,
                children: filteredChildren,
                substeps: filteredSubsteps,
              };
            })
            .filter(Boolean) || [];

        if (filteredSteps.length === 0) return null;
        return {
          ...section,
          steps: filteredSteps,
          hideSectionHeader: true,
        };
      })
      .filter(Boolean);
  };

  // Apply filterContentBySelection when we have selected nodes (focus on moved/copied branch)
  const unfilteredContentData = processedData
    .map((section) => ({
      ...section,
      hideSectionHeader: false,
    }))
    .filter(Boolean);

  // move-copy-delete-preview: show ALL nodes - no filtering
  // Child node move/copy (RICH_TEXT, VIDEO, CANVAS, ATTACHMENT): show full tree so both views show where item was vs where it is (different section/step)
  // move-preview (section/step move): filter to focused branch when nodes are selected
  const actionStr = normalizeActionTokens(
    audit_trail_data?.version_content?.action_to_show,
  );
  const inferChildNodeCopyType = (s) => {
    const t = inferBulkMovedNodeType(s);
    return (
      t === "rich-text" ||
      t === "video" ||
      t === "canvas" ||
      t === "attachment"
    );
  };
  const isChildNodeMoveCopy =
    actionStr.includes("RICH_TEXT_") ||
    actionStr.includes("VIDEO_") ||
    actionStr.includes("CANVAS_") ||
    actionStr.includes("CANVA_") ||
    actionStr.includes("ATTACHMENT_") ||
    inferChildNodeCopyType(audit_trail_data?.version_content?.action_to_show) ||
    inferChildNodeCopyType(audit_trail_data?.action);
  const snapshotMetaForFilter =
    selectedView === "new" ? newContent.meta : originalContent.meta;
  const vcSnap = audit_trail_data?.version_content || {};
  const pvcSnap = audit_trail_data?.parent_version_data?.version_content || {};
  /** Prefer shared resolver so CREATE_STEP matches version_content.step_tree_node_id when meta omits tree_node_id */
  const snapshotTreeNodeId = resolveSnapshotTreeNodeIdForAudit(
    audit_trail_data,
    selectedView,
    originalContent.meta,
    newContent.meta,
  );

  const narrowHint = `${audit_trail_data?.version_content?.action_to_show || ""} ${audit_trail_data?.action || ""}`;
  const snapshotNodeTypeRaw =
    resolveSnapshotNodeTypeForAudit(
      audit_trail_data,
      selectedView,
      originalContent.meta,
      newContent.meta,
    ) ||
    snapshotMetaForFilter?.type ||
    newContent.meta?.type ||
    originalContent.meta?.type ||
    vcSnap.type ||
    pvcSnap.type ||
    inferBulkMovedNodeType(vcSnap.action_to_show) ||
    inferBulkMovedNodeType(audit_trail_data?.action);
  const snapshotNodeType =
    snapshotNodeTypeRaw === "canva"
      ? "canvas"
      : snapshotNodeTypeRaw === "rich_text"
        ? "rich-text"
        : snapshotNodeTypeRaw;
  const allowNarrowToSingleNodeSnapshot =
    snapshotMetaForFilter?.operation === "single_audit_trail" ||
    newContent.meta?.operation === "single_audit_trail" ||
    originalContent.meta?.operation === "single_audit_trail" ||
    snapshotMetaForFilter?.operation === "bulk_audit_trail" ||
    newContent.meta?.operation === "bulk_audit_trail" ||
    originalContent.meta?.operation === "bulk_audit_trail" ||
    /SAVE\s+SECTION|CREATE_SECTION|UPDATE_SECTION|EDIT\s+SECTION/i.test(
      narrowHint,
    ) ||
    /SAVE\s+STEP|CREATE_STEP|UPDATE_STEP|EDIT\s+STEP/i.test(narrowHint) ||
    /SAVE\s+SUBSTEP|CREATE_SUBSTEP|UPDATE_SUBSTEP|EDIT\s+SUBSTEP/i.test(
      narrowHint,
    ) ||
    /SAVE\s+ATTACHMENT|CREATE_ATTACHMENT|UPDATE_ATTACHMENT|EDIT\s+ATTACHMENT/i.test(
      narrowHint,
    ) ||
    /SAVE\s+CANVAS|CREATE_CANVAS|UPDATE_CANVAS|EDIT\s+CANVAS/i.test(narrowHint) ||
    /SAVE\s+RICH\s+TEXT|CREATE_RICH_TEXT|UPDATE_RICH_TEXT|EDIT\s+RICH\s+TEXT/i.test(
      narrowHint,
    ) ||
    /SAVE\s+VIDEO|CREATE_VIDEO|UPDATE_VIDEO|EDIT\s+VIDEO/i.test(narrowHint) ||
    /* Child node move/copy: snapshot may omit operation; action_to_show is e.g. VIDEO_COPIED (not matched by CREATE_VIDEO) */
    /VIDEO_(?:MOVED|COPIED)/i.test(narrowHint) ||
    /(?:CANVAS|CANVA)_(?:MOVED|COPIED)/i.test(narrowHint) ||
    /ATTACHMENT_(?:MOVED|COPIED)/i.test(narrowHint) ||
    /(?:RICH_TEXT|RICH-TEXT)_(?:MOVED|COPIED)/i.test(narrowHint) ||
    /SECTION_(?:MOVED|COPIED|DELETED)/i.test(narrowHint) ||
    /* Human: "User Copied Canvas …", "User Copied Rich Text …", "User Copied Video …" (no *_COPIED token) */
    /\b(?:User\s+)?(?:Copied|Moved)\s+(?:Canvas|Video|Rich\s+Text|Attachment)\b/i.test(
      narrowHint,
    ) ||
    /\bUser\s+(?:Moved|Copied|Deleted)\s+Section\s+\d+/i.test(narrowHint) ||
    /\b(?:Copy|Move|Delete)\s+Section\s+\d+/i.test(narrowHint);

  const preserveSectionChromeForBulkStep =
    snapshotMetaForFilter?.operation === "bulk_audit_trail" ||
    newContent.meta?.operation === "bulk_audit_trail" ||
    originalContent.meta?.operation === "bulk_audit_trail";

  const narrowSnapshotTree = (sections) => {
    if (
      !allowNarrowToSingleNodeSnapshot ||
      !snapshotTreeNodeId ||
      !Array.isArray(sections)
    ) {
      return null;
    }
    return narrowProcedureTreeBySnapshotId(
      sections,
      snapshotTreeNodeId,
      snapshotNodeType,
      preserveSectionChromeForBulkStep
        ? { preserveSectionChromeForStepFocus: true }
        : undefined,
    );
  };

  let contentData = unfilteredContentData;
  if (
    !isMoveCopyDeletePreview &&
    !isChildNodeMoveCopy &&
    Object.keys(selectedNodes).length > 0
  ) {
    /* Narrow first: filterContentBySelection keeps section chrome and all steps when section id is selected */
    const narrowedWithSelection = narrowSnapshotTree(unfilteredContentData);
    if (narrowedWithSelection && narrowedWithSelection.length > 0) {
      contentData = narrowedWithSelection;
    } else {
      const filtered = filterContentBySelection(unfilteredContentData);
      if (filtered && filtered.length > 0) {
        contentData = filtered;
      } else {
        contentData = unfilteredContentData;
      }
    }
  } else if (
    !isMoveCopyDeletePreview &&
    !isChildNodeMoveCopy &&
    allowNarrowToSingleNodeSnapshot &&
    snapshotTreeNodeId &&
    Array.isArray(unfilteredContentData)
  ) {
    const narrowed = narrowSnapshotTree(unfilteredContentData);
    if (narrowed) contentData = narrowed;
  }

  // Child-node moves previously skipped narrowing so the full tree stayed visible. For
  // single_audit_trail (and similar), narrow to the section/step that contains the moved
  // node so unrelated sections are hidden while the attachment (or rich-text, etc.) still shows.
  if (
    isChildNodeMoveCopy &&
    !isMoveCopyDeletePreview &&
    allowNarrowToSingleNodeSnapshot &&
    snapshotTreeNodeId &&
    Array.isArray(contentData)
  ) {
    const narrowed = narrowSnapshotTree(contentData);
    if (narrowed && narrowed.length > 0) contentData = narrowed;
  }

  // For child node moves (attachment, video, canvas, rich-text), use the (possibly narrowed) tree
  const nodeContentData =
    normalizedContentType === "video" ||
    normalizedContentType === "rich-text" ||
    normalizedContentType === "canvas" ||
    normalizedContentType === "attachment"
      ? contentData
      : null;

  const finalContentData =
    nodeContentData !== null ? nodeContentData : contentData;

  const hasInlineSnapshot =
    Array.isArray(
      dataForHeader?.version_content?.originingalContent ??
        dataForHeader?.version_content?.originalContent,
    ) && Array.isArray(dataForHeader?.version_content?.newContent);

  const missingSnapshotForSelectedView =
    selectedView === "new"
      ? !dataForHeader?.new_content_snapshot
      : !dataForHeader?.previous_content_snapshot;

  const showNoData =
    isContentLoaded &&
    !hasInlineSnapshot &&
    missingSnapshotForSelectedView &&
    (!Array.isArray(finalContentData) || finalContentData.length === 0);

  // Use full tree view for child nodes too (Section > Step > Child) - no separate flat node preview
  const isNodePreview = false;

  const nodeTitle =
    titleSource?.pageTitle ||
    titleSource?.elementTitle ||
    titleSource?.name ||
    (typeof titleSource?.breadcrumb_text === "string"
      ? titleSource.breadcrumb_text.split(" > ").pop()
      : null) ||
    "Item";

  const nodeShortDescription = titleSource?.short_description || "";
  const nodeVideoUrl =
    titleSource?.video || titleSource?.video_http_url || titleSource?.video_url;
  const nodeVideoUrlField =
    titleSource?.video_url_field || titleSource?.video_url || "";
  const canvasPages = Array.isArray(titleSource?.pages)
    ? titleSource.pages
    : [];
  const richTextCategory =
    getRichTextCategoryDisplayValue(titleSource) ||
    getRichTextCategoryDisplayValue(vcMergedForHeader) ||
    getRichTextCategoryDisplayValue(activeSnapshotMeta) ||
    "";
  const rawRichTextContent = titleSource?.rich_text_content || "";
  const richTextContent =
    rawRichTextContent && rawRichTextContent.includes('class="editor-shell"')
      ? rawRichTextContent.replace(
          /^<div class="editor-shell">(.*)<\/div>\s*$/s,
          "$1",
        )
      : rawRichTextContent;

  let attachmentList = [];
  if (normalizedContentType === "attachment") {
    try {
      if (typeof titleSource?.attachments === "string") {
        attachmentList = JSON.parse(titleSource.attachments);
      } else if (Array.isArray(titleSource?.attachments)) {
        attachmentList = titleSource.attachments;
      }
    } catch (e) {
      attachmentList = [];
    }
    attachmentList = (attachmentList || []).filter(Boolean);
  }

  const vc = audit_trail_data.version_content || {};
  const parentVc = audit_trail_data.parent_version_data?.version_content || {};
  const actionForTitle = (vc.action_to_show || "").toUpperCase();
  const actionMessage = typeof audit_trail_data.action === "string" ? audit_trail_data.action : "";

  const getSectionLabel = (src) => src?.section || (typeof src?.breadcrumb_text === "string" && (() => { const parts = src.breadcrumb_text.split(" > "); return parts.find((p) => p && p.match(/^Section\s+\d+$/i)); })()) || (src?.pageTitle && src.pageTitle.match(/^Section\s+\d+$/i) ? src.pageTitle : null) || (src?.sibling_order != null ? `Section ${src.sibling_order}` : null);

  const getDisplayTitleBase = () => {
    const msg = (actionMessage || "").trim();
    // Primary title from message start: "User Moved/Copied Step N into|above|from ..." -> "Step N" (same for Substep/Section) even when action_to_show is child op (e.g. VIDEO_MOVED)
    const userMovedStep = msg.match(/^User\s+(?:Moved|Copied)\s+(Step\s+\d+)(?:\s+into|\s+above|\s+from)/i);
    if (userMovedStep) return userMovedStep[1];
    const userMovedSubstep = msg.match(/^User\s+(?:Moved|Copied)\s+(Substep\s+[\dA-Za-z]+)(?:\s+into|\s+above|\s+from)/i);
    if (userMovedSubstep) return userMovedSubstep[1];
    // Fallback: "User Copied Substep 1B from Step 1 of Section 1 in X, into ..."
    const userSubstepAlt = msg.match(/^User\s+(?:Moved|Copied)\s+(Substep\s+[\dA-Za-z]+)\s+from/i);
    if (userSubstepAlt) return userSubstepAlt[1];
    const userMovedSection = msg.match(/^User\s+(?:Moved|Copied)\s+(Section\s+\d+)(?:\s+into|\s+above|\s+from)/i);
    if (userMovedSection) return userMovedSection[1];
    // "... System Performs Operation 2 of 6, Copy Step 1 into Section 1 in …" (new revision bulk)
    const sysCopyStepIntoSec = msg.match(
      /\bcopy\s+(step\s+\d+)\s+into\s+section\s+\d+/i,
    );
    if (sysCopyStepIntoSec) return sysCopyStepIntoSec[1];
    const sysCopyStep = msg.match(/\bcopy\s+(step\s+\d+)\b/i);
    if (sysCopyStep) return sysCopyStep[1];
    const sysCopySubInto = msg.match(/\bcopy\s+(substep\s+[\w\d]+)\s+into\b/i);
    if (sysCopySubInto) return sysCopySubInto[1];
    const sysCopySub = msg.match(/\bcopy\s+(substep\s+[\w\d]+)\b/i);
    if (sysCopySub) return sysCopySub[1];
    const userDeleted = msg.match(/^(?:User\s+)?Deleted\s+(Section\s+\d+|Step\s+\d+|Substep\s+[\dA-Za-z]+)/i);
    if (userDeleted) return userDeleted[1];
    // Deleted child node: "Deleted Attachment 3 from ..." or "User Deleted Video 2 from ..." (optional "User ")
    const deletedAttachment = msg.match(/^(?:User\s+)?Deleted\s+(Attachment\s+\d+)(?:\s+from)?/i);
    if (deletedAttachment) return deletedAttachment[1];
    const deletedVideo = msg.match(/^(?:User\s+)?Deleted\s+(Video\s+\d+)(?:\s+from)?/i);
    if (deletedVideo) return deletedVideo[1];
    const deletedCanvas = msg.match(/^(?:User\s+)?Deleted\s+(Canvas\s+\d+)(?:\s+from)?/i);
    if (deletedCanvas) return deletedCanvas[1];
    const deletedRichText = msg.match(/^(?:User\s+)?Deleted\s+(Rich\s+Text\s+\d+)(?:\s+from)?/i);
    if (deletedRichText) return deletedRichText[1];
    // Child node move/copy: "User Moved Attachment 1..." or "Moved Rich Text 2 from..." (optional "User ") -> show "Attachment 1" / "Rich Text 2"
    const userMovedAttachment = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Attachment\s+\d+)(?:\s+from|\s+into)/i);
    if (userMovedAttachment) return userMovedAttachment[1];
    const userMovedVideo = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Video\s+\d+)(?:\s+from|\s+into)/i);
    if (userMovedVideo) return userMovedVideo[1];
    const userMovedCanvas = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Canvas\s+\d+)(?:\s+from|\s+into)/i);
    if (userMovedCanvas) return userMovedCanvas[1];
    const userMovedRichText = msg.match(/^(?:User\s+)?(?:Moved|Copied)\s+(Rich\s+Text\s+\d+)(?:\s+from|\s+into)/i);
    if (userMovedRichText) return userMovedRichText[1];

    const sectionLabel = getSectionLabel(vc) || getSectionLabel(parentVc);
    const hasSectionContext = (vc.section_tree_node_id || parentVc.section_tree_node_id) && (vc.section || parentVc.section || (typeof (vc.breadcrumb_text || parentVc.breadcrumb_text) === "string" && (vc.breadcrumb_text || parentVc.breadcrumb_text).split(" > ").some((p) => p && p.match(/^Section\s+\d+$/i))));

    // When message says "User Moved Step 2" (or "Move Step 2 in Section"), show "Step 2" even if action_to_show includes SUBSTEP
    if (actionForTitle.includes("STEP") && (actionMessage.match(/User\s+Moved\s+Step\s+\d+/i) || actionMessage.match(/Move\s+Step\s+\d+\s+in\s+Section/i))) {
      const stepFromMsg = actionMessage.match(/(?:User\s+)?(?:Moved|Copied)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/Move\s+(Step\s+\d+)\s+in\s+Section/i)?.[1];
      if (stepFromMsg) return stepFromMsg;
    }

    if (actionForTitle.includes("STEP") && !actionForTitle.includes("SUBSTEP")) {
      const stepFromMsg = actionMessage.match(/(?:User\s+)?(?:Moved|moved|Copied|copied)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/Move\s+(Step\s+\d+)\s+in\s+Section/i)?.[1] || actionMessage.match(/(Step\s+\d+)/i)?.[1];
      if (stepFromMsg) return stepFromMsg;
      const stepOnly = (s) => (s && typeof s === "string" && s.match(/^Step\s+\d+$/i)) ? s : null;
      const stepLabel = vc.stepTitle || parentVc.stepTitle || stepOnly(vc.pageTitle) || stepOnly(parentVc.pageTitle);
      if (stepLabel) return stepLabel;
      return "Step";
    }
    if (actionForTitle.includes("SUBSTEP")) {
      const substepOnly = (s) => (s && typeof s === "string" && s.match(/^Substep\s+[\dA-Za-z]+$/i)) ? s : null;
      const substepLabel = vc.substepTitle || parentVc.substepTitle || substepOnly(vc.pageTitle) || substepOnly(parentVc.pageTitle);
      if (substepLabel) return substepLabel;
      const saveEditSub = actionMessage.match(
        /\b(?:Save|Update|Edit|Create|Created|User\s+Edited|Updated)\s+(Substep\s+[\dA-Za-z]+)\b/i,
      )?.[1];
      if (saveEditSub) return saveEditSub;
      const substepFromMsg = actionMessage.match(/(?:Moved|moved|Copied|copied)\s+(Substep\s+[\dA-Za-z]+)/i)?.[1] || actionMessage.match(/(Substep\s+[\dA-Za-z]+)/i)?.[1];
      if (substepFromMsg) return substepFromMsg;
      return "Substep";
    }
    if (actionForTitle.includes("SECTION") || hasSectionContext) {
      if (sectionLabel) return sectionLabel;
      if (hasSectionContext) return "Section";
    }
    if (actionForTitle.includes("DELETE")) {
      if (sectionLabel) return sectionLabel;
      const stepLabel = vc.stepTitle || parentVc.stepTitle || vc.pageTitle || parentVc.pageTitle;
      if (stepLabel && (stepLabel.match(/^Step\s+\d+$/i) || (actionForTitle.includes("STEP") && !actionForTitle.includes("SUBSTEP")))) return stepLabel;
      const substepLabel = vc.substepTitle || parentVc.substepTitle || vc.pageTitle || parentVc.pageTitle;
      if (substepLabel && (substepLabel.match(/^Substep\s+[\dA-Z]+$/i) || actionForTitle.includes("SUBSTEP"))) return substepLabel;
      if (vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name) return vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name;
      const sectionFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Section\s+\d+)/i)?.[1] || actionMessage.match(/(Section\s+\d+)/i)?.[1];
      if (sectionFromMsg) return sectionFromMsg;
      const stepFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Step\s+\d+)/i)?.[1] || actionMessage.match(/(Step\s+\d+)/i)?.[1];
      if (stepFromMsg) return stepFromMsg;
      const substepFromMsg = actionMessage.match(/(?:Deleted|deleted)\s+(Substep\s+[\dA-Za-z]+)/i)?.[1] || actionMessage.match(/(Substep\s+[\dA-Za-z]+)/i)?.[1];
      if (substepFromMsg) return substepFromMsg;
    }
    // Video, Attachment, Rich Text, Canvas (edit/move/copy) - use pageTitle/elementTitle/name; fallback to parent for edit entries
    const isChildNode = actionForTitle.includes("VIDEO") || actionForTitle.includes("ATTACHMENT") || actionForTitle.includes("RICH_TEXT") || actionForTitle.includes("RICH-TEXT") || actionForTitle.includes("CANVAS") || actionForTitle.includes("CANVA") || ["video", "attachment", "rich-text", "canvas"].includes(vc.type);
    if (isChildNode) {
      const label = vc.pageTitle || vc.elementTitle || vc.name || parentVc.pageTitle || parentVc.elementTitle || parentVc.name ||
        (typeof vc.breadcrumb_text === "string" ? vc.breadcrumb_text.split(" > ").pop() : null) ||
        (typeof parentVc.breadcrumb_text === "string" ? parentVc.breadcrumb_text.split(" > ").pop() : null);
      if (label) return label;
      const typeLabel = vc.type === "canva" ? "Canvas" : (vc.type || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return typeLabel || "Item";
    }
    return (
      vc.pageTitle ||
      vc.elementTitle ||
      vc.stepTitle ||
      vc.section ||
      vc.partNumber ||
      "Item"
    );
  };

  const displayTitleBase = getDisplayTitleBase();
  const displayTitle = audit_trail_data.version_id
    ? `${displayTitleBase} (Version ${audit_trail_data.version_id}) Review`
    : "Loading...";

  const auditSubstepCopyLabels =
    parseAuditSubstepCopyMoveSourceLabels(actionMessage);
  const auditSubstepSaveLabels = parseAuditSubstepSaveEditLabels(
    actionMessage,
    audit_trail_data.comment,
  );
  const auditStepSaveLabels = parseAuditStepSaveActionLabels(
    actionMessage,
    audit_trail_data.comment,
  );
  const auditSectionCopyLabels =
    parseAuditSectionCopyMoveLabels(actionMessage);
  const titleBaseTrim =
    typeof displayTitleBase === "string" ? displayTitleBase.trim() : "";
  const titleDerivedSectionLabel = (() => {
    const m = titleBaseTrim.match(/^Section\s+(\d+)\b/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? `Section ${n}` : null;
  })();
  const titleDerivedStepOrdinal = (() => {
    const m = titleBaseTrim.match(/^Step\s+(\d+)\b/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? n : null;
  })();
  const titleDerivedSubstepHeading = (() => {
    const m = titleBaseTrim.match(/^(Substep\s+[\dA-Za-z]+)\b/i);
    return m ? m[1] : null;
  })();
  const titleDerivedSubstepStepOrdinal = (() => {
    const m = titleBaseTrim.match(/^Substep\s+(\d+)/i);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return !Number.isNaN(n) && n >= 1 ? n : null;
  })();
  const previewAuditSubstepHeading =
    auditSubstepCopyLabels?.substepHeading ??
    auditSubstepSaveLabels?.substepHeading ??
    titleDerivedSubstepHeading ??
    null;
  const previewNarrowedSingleStep =
    Array.isArray(finalContentData) &&
    finalContentData.length === 1 &&
    finalContentData[0]?.hideSectionHeader === true &&
    finalContentData[0]?.steps?.length === 1;
  const isEmbeddedAuditPreview =
    hideAppBar === true && externalAuditTrailData != null;
  const previewAuditStepOrdinalMerged =
    auditSubstepCopyLabels?.sourceStepOrdinal ??
    auditSubstepSaveLabels?.stepOrdinal ??
    titleDerivedSubstepStepOrdinal ??
    auditStepSaveLabels?.stepOrdinal ??
    titleDerivedStepOrdinal ??
    null;
  const previewAuditStepOrdinalOverride =
    isEmbeddedAuditPreview || previewNarrowedSingleStep
      ? previewAuditStepOrdinalMerged
      : auditSubstepCopyLabels?.sourceStepOrdinal ?? null;
  const previewAuditSectionLabelOverride =
    isEmbeddedAuditPreview || previewNarrowedSingleStep
      ? auditSubstepCopyLabels?.sourceSectionLabel ??
        auditSubstepSaveLabels?.sectionLabel ??
        auditStepSaveLabels?.sectionLabel ??
        auditSectionCopyLabels?.sectionLabel ??
        titleDerivedSectionLabel ??
        null
      : auditSubstepCopyLabels?.sourceSectionLabel ??
        auditSectionCopyLabels?.sectionLabel ??
        titleDerivedSectionLabel ??
        null;
  const auditNeedsTreeFocus =
    isEmbeddedAuditPreview &&
    (previewAuditStepOrdinalOverride != null ||
      previewAuditSubstepHeading != null);
  const snapshotFocusTidForPreview = auditNeedsTreeFocus
    ? resolveSnapshotTreeNodeIdForAudit(
        audit_trail_data,
        selectedView,
        originalContent.meta,
        newContent.meta,
      )
    : null;
  const auditFocusStepTreeNodeId = auditNeedsTreeFocus
    ? snapshotFocusTidForPreview ||
      vc.sub_step_tree_node_id ||
      vc.substep_tree_node_id ||
      vc.step_tree_node_id ||
      vc.preview_tree_node_id ||
      vc.element_id ||
      vc.procedure_tree_ref ||
      vc.tree_node_id ||
      null
    : null;
  const auditOrdinalFallbackSectionNumber = (() => {
    const lab = previewAuditSectionLabelOverride;
    if (!lab || typeof lab !== "string") return 1;
    const m = lab.match(/Section\s+(\d+)/i);
    return m ? parseInt(m[1], 10) : 1;
  })();

  /** Same routes as ProcedureStatusInfo showDisabledCheckboxes — tree uses read-only blue accent, not native disabled grey */
  const auditTrailPreviewHeaderDisabledCheckboxes =
    (location.pathname.includes("/move-preview/") &&
      !location.pathname.includes("move-copy-delete")) ||
    (location.pathname.includes("/audit-trail/preview/") &&
      !location.pathname.includes("move-preview"));

  /** `/audit-trail/preview/:id` only — hide procedure header checkbox; keep tree read-only styling */
  const isAuditTrailStandalonePreview =
    location.pathname.includes("/audit-trail/preview/") &&
    !location.pathname.includes("move-preview");

  /** Same as ReviewLayout: node heading already says "Rich Text N" — hide form-field captions */
  const hideRichTextFieldLabelsAuditTrailPreview =
    location.pathname.includes("/audit-trail/preview/") ||
    location.pathname.includes("/audit-trail/preview-current-node/") ||
    location.pathname.includes("/audit-trail/preview-deleted-node/");

  const embeddedProcedureAppBarTitleBase =
    hideAppBar === true && externalAuditTrailData != null
      ? typeof procedureAppBarTitleBase === "string" &&
        procedureAppBarTitleBase.trim() !== ""
        ? procedureAppBarTitleBase.trim()
        : null
      : null;
  const readOnlyCheckboxTitleBase =
    auditTrailPreviewHeaderDisabledCheckboxes
      ? embeddedProcedureAppBarTitleBase ?? titleBaseTrim
      : undefined;

  const handleReviewAgainstDifferentVersion = async () => {
    await dispatch(
      getAllVersionHistories({ versionId: audit_trail_data.version_id }),
    );
    setShowVersionHistory(true);
  };

  return (
    <div style={{ paddingBottom: "10px" }}>
      {!hideAppBar && (
        <>
          <ProcedureMoveAppBar
            selectedView={selectedView}
            showBackButton={false}
            setSelectedView={setSelectedView}
            title={displayTitle}
            titleFontSize="18px"
            buttons={[
              {
                label: isLoadingVersions
                  ? "Loading..."
                  : "Review Against Different Version",
                onClick: handleReviewAgainstDifferentVersion,
              },
            ]}
          />
        </>
      )}

      {loading ? (
        <Loader />
      ) : (
        <>
          <ProcedureStatusInfo
            item_code_type={
              vcMergedForHeader?.item_code_type ??
              dataForHeader.parent_version_data?.version_content?.item_code_type ??
              dataForHeader.version_content?.version_content?.item_code_type ??
              dataForHeader.version_content?.item_code_type ??
              dataForHeader.procedure_revision?.item_code_type ??
              activeSnapshotMeta?.item_code_type ??
              procedureCategoryLabelFromVc(vcMergedForHeader) ??
              procedureCategoryLabelFromVc(dataForHeader.procedure_revision)
            }
            partNumber={
              vcMergedForHeader?.partNumber ??
              vcMergedForHeader?.part_number ??
              dataForHeader.parent_version_data?.version_content?.partNumber ??
              dataForHeader.parent_version_data?.version_content?.part_number ??
              dataForHeader.version_content?.version_content?.partNumber ??
              dataForHeader.version_content?.version_content?.part_number ??
              dataForHeader.version_content?.partNumber ??
              dataForHeader.version_content?.part_number ??
              dataForHeader.procedure_revision?.part_number ??
              dataForHeader.procedure_revision?.partNumber ??
              dataForHeader.partNumber ??
              dataForHeader.part_number ??
              activeSnapshotMeta?.partNumber ??
              activeSnapshotMeta?.part_number
            }
            status={displayStatus}
            revision={displayRevision}
            partDescription={
              dataForHeader.parent_version_data?.version_content?.partDescription ??
              dataForHeader.version_content?.version_content?.partDescription ??
              dataForHeader.version_content?.partDescription
            }
            showDisabledCheckboxes={auditTrailPreviewHeaderDisabledCheckboxes}
            showProcedureHeaderCheckbox={!isAuditTrailStandalonePreview}
            partNumberMarginLeft={isMoveCopyDeletePreview}
            whenSaved={isMoveCopyDeletePreview ? undefined : dataForHeader?.audit_date}
          />

          {!isContentLoaded ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <div>Loading...</div>
            </div>
          ) : showNoData ? (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#666",
                fontSize: "16px",
              }}
            >
              No Data
            </div>
          ) : isNodePreview ? (
            <div style={{ padding: "20px" }}>
              <div
                style={{ marginTop: "20px", marginBottom: "20px" }}
                className="header-spacing"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  {auditTrailPreviewHeaderDisabledCheckboxes ? (
                    <AccentReadOnlyCheckbox
                      readOnlyVisual
                      checked={
                        selectedNodes?.[effectiveTargetNodeId] || false
                      }
                      style={{ marginTop: "2px" }}
                    />
                  ) : (
                    <input
                      type="checkbox"
                      checked={selectedNodes?.[effectiveTargetNodeId] || false}
                      onChange={() =>
                        effectiveTargetNodeId &&
                        handleNodeToggle(effectiveTargetNodeId)
                      }
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        marginTop: "2px",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, width: "100%" }}>
                    <h3 style={{ margin: 0 }}>{nodeTitle}</h3>
                  </div>
                </div>
                <hr
                  style={{
                    borderColor: "#d3d3d3",
                    backgroundColor: "#d3d3d3",
                    color: "#d3d3d3",
                  }}
                />

                {normalizedContentType === "video" && nodeVideoUrl && (
                  <div style={{ marginTop: "10px" }}>
                    <video
                      controls
                      style={{
                        width: "100%",
                        maxHeight: "500px",
                        border: "1px solid #ccc",
                        objectFit: "contain",
                        backgroundColor: "#000",
                        borderRadius: "6px",
                      }}
                    >
                      <source src={nodeVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {normalizedContentType === "video" && nodeVideoUrlField && (
                  <div style={{ marginTop: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <label style={{ fontWeight: 600 }}>Video URL</label>
                    </div>
                    <input
                      type="text"
                      value={nodeVideoUrlField}
                      disabled
                      style={{
                        width: "100%",
                        border: "1px solid #d3d3d3",
                        padding: "8px",
                        marginTop: "8px",
                      }}
                    />
                  </div>
                )}

                {normalizedContentType === "canvas" &&
                  canvasPages.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      {canvasPages.map((src, idx) => (
                        <img
                          key={`${src}-${idx}`}
                          src={src}
                          alt={`Canvas page ${idx + 1}`}
                          style={{
                            width: "100%",
                            maxHeight: "500px",
                            objectFit: "contain",
                            border: "1px solid #ccc",
                            borderRadius: "6px",
                            marginBottom: "10px",
                          }}
                        />
                      ))}
                    </div>
                  )}

                {normalizedContentType === "rich-text" && (
                  <div
                    className="audit-trail-richtext-fields"
                    style={{ marginTop: "8px" }}
                  >
                    {nodeShortDescription != null && nodeShortDescription !== "" && (
                      <div
                        className="richtext-editor-container"
                        style={{
                          paddingLeft: hideRichTextFieldLabelsAuditTrailPreview
                            ? 0
                            : 10,
                          paddingBottom: 0,
                          marginTop: 0,
                        }}
                      >
                        {!hideRichTextFieldLabelsAuditTrailPreview ? (
                        <label className="richtext-editor-label">
                          Short Description
                        </label>
                        ) : null}
                        <div
                          className="richtext-editor-wrapper"
                          style={{ overflowX: "auto" }}
                        >
                          <div
                            className="rich-text-content-display"
                            style={{ padding: "0 0" }}
                          >
                            <RenderHtml
                              content={normalizeRichTextFieldForRenderHtml(
                                nodeShortDescription,
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {richTextCategory ? (
                      hideRichTextFieldLabelsAuditTrailPreview ? (
                        <div style={{ marginTop: "5px" }}>{richTextCategory}</div>
                      ) : (
                        <div
                          style={{
                            marginLeft: "10px",
                            marginTop: "12px",
                            fontSize: "14px",
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>Rich Text Category </span>
                          {richTextCategory}
                        </div>
                      )
                    ) : null}
                    {richTextContent ? (
                      <div
                        className="richtext-editor-container"
                        style={{
                          paddingLeft: hideRichTextFieldLabelsAuditTrailPreview
                            ? 0
                            : 10,
                          paddingBottom: 0,
                          marginTop: hideRichTextFieldLabelsAuditTrailPreview
                            ? "5px"
                            : nodeShortDescription != null &&
                                nodeShortDescription !== ""
                              ? "16px"
                              : 0,
                        }}
                      >
                        {!hideRichTextFieldLabelsAuditTrailPreview ? (
                        <label className="richtext-editor-label">
                          Rich text content
                        </label>
                        ) : null}
                        <div
                          className="richtext-editor-wrapper"
                          style={{ overflowX: "auto" }}
                        >
                          <div
                            className="rich-text-content-display"
                            style={{
                              fontSize: "14px",
                              padding: "12px 0",
                              minWidth: 0,
                            }}
                          >
                            <RenderHtml
                              content={normalizeRichTextFieldForRenderHtml(
                                richTextContent,
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                {normalizedContentType === "attachment" &&
                  attachmentList.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      {attachmentList.map((attachment, idx) => (
                        <div
                          key={`${String(attachment)}-${idx}`}
                          style={{ marginLeft: "20px" }}
                        >
                          {renderPreview(attachment)}
                        </div>
                      ))}
                    </div>
                  )}

                {nodeShortDescription &&
                  normalizedContentType !== "rich-text" && (
                    <div
                      style={{ marginBottom: "16px", whiteSpace: "pre-wrap" }}
                    >
                      {nodeShortDescription}
                    </div>
                  )}
              </div>
            </div>
          ) : finalContentData && finalContentData.length > 0 ? (
            <div style={{ padding: "20px" }}>
              <PreviewContent
                contentData={finalContentData}
                procedureTreeForStepNumbering={unfilteredContentData}
                showCheckboxes={!isMoveCopyDeletePreview}
                disabled={auditTrailPreviewHeaderDisabledCheckboxes}
                selectedNodes={selectedNodes}
                onNodeToggle={handleNodeToggle}
                focusNodeId={undefined} // Ensure no filtering occurs
                auditSubstepHeading={previewAuditSubstepHeading}
                auditStepOrdinalOverride={previewAuditStepOrdinalOverride}
                auditSourceSectionLabel={previewAuditSectionLabelOverride}
                auditFocusStepTreeNodeId={auditFocusStepTreeNodeId}
                auditOrdinalIndexFallback={isEmbeddedAuditPreview}
                auditOrdinalFallbackSectionNumber={
                  auditOrdinalFallbackSectionNumber
                }
                readOnlyCheckboxTitleBase={readOnlyCheckboxTitleBase}
                hideRichTextFieldLabels={hideRichTextFieldLabelsAuditTrailPreview}
                breadcrumbText={(() => {
                let breadcrumb = newContent.meta?.breadcrumb_text;
                // If breadcrumb is still undefined, try to extract it from the rendered content
                if (!breadcrumb && processedData && processedData.length > 0) {
                  // Look for video content with short_description
                  for (let sectionIndex = 0; sectionIndex < processedData.length; sectionIndex++) {
                    const section = processedData[sectionIndex];
                    if (section.steps) {
                      for (let stepIndex = 0; stepIndex < section.steps.length; stepIndex++) {
                        const step = section.steps[stepIndex];
                        if (step.substeps) {
                          for (let substepIndex = 0; substepIndex < step.substeps.length; substepIndex++) {
                            const substep = step.substeps[substepIndex];
                            if (substep.children) {
                              for (let childIndex = 0; childIndex < substep.children.length; childIndex++) {
                                const child = substep.children[childIndex];
                                if (child.type === 'video') {
                                  // Check for short_description in child itself or in version_content
                                  let shortDescription = child.short_description;
                                  if (!shortDescription && child.version_content) {
                                    shortDescription = child.version_content.short_description;
                                  }
                                  if (!shortDescription && child.version_details?.version_content) {
                                    shortDescription = child.version_details.version_content.short_description;
                                  }
                                  if (!shortDescription && child.detail) {
                                    shortDescription = child.detail.short_description;
                                  }
                                  if (!shortDescription && child.content) {
                                    shortDescription = child.content.short_description;
                                  }
                                  
                                  if (shortDescription) {
                                    const htmlMatch = shortDescription.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
                                    if (htmlMatch) {
                                      breadcrumb = htmlMatch[0].replace(/&gt;/g, '>');
                                    } else {
                                      // If HTML extraction fails, try to build breadcrumb from section/step/substep IDs
                                      const sectionId = child.section_id_ref;
                                      const stepId = child.step_id_ref;
                                      const substepId = child.substep_id_ref;
                                      
                                      // Find the section, step, and substep in processedData to get their numbers
                                      let sectionNumber = null;
                                      let stepNumber = null;
                                      let substepLetter = null;
                                      
                                      // Find section
                                      for (let s = 0; s < processedData.length; s++) {
                                        const section = processedData[s];
                                        if (section.tree_node_id === sectionId || section.section_id_ref === sectionId) {
                                          sectionNumber = s + 1; // Sections are 1-indexed
                                          
                                          // Find step in this section
                                          if (section.steps && stepId) {
                                            for (let st = 0; st < section.steps.length; st++) {
                                              const step = section.steps[st];
                                              if (step.tree_node_id === stepId || step.step_id_ref === stepId) {
                                                stepNumber = st + 1; // Steps are 1-indexed
                                                
                                                // Find substep in this step
                                                if (step.substeps && substepId) {
                                                  for (let ss = 0; ss < step.substeps.length; ss++) {
                                                    const substep = step.substeps[ss];
                                                    if (substep.tree_node_id === substepId || substep.substep_id_ref === substepId) {
                                                      substepLetter = String.fromCharCode(65 + ss); // A=0, B=1, etc.
                                                      break;
                                                    }
                                                  }
                                                }
                                                break;
                                              }
                                            }
                                          }
                                          break;
                                        }
                                      }
                                      
                                      // Build breadcrumb from extracted numbers
                                      if (sectionNumber && stepNumber) {
                                        breadcrumb = `Section ${sectionNumber} > Step ${stepNumber}`;
                                        if (substepLetter) {
                                          breadcrumb += ` > Substep ${stepNumber}${substepLetter}`;
                                        }
                                      } else {
                                      }
                                    }
                                    
                                    if (breadcrumb) {
                                      // Store breadcrumb globally for PreviewLayout to access
                                      window.lastExtractedBreadcrumb = breadcrumb;
                                      // Store video node data globally for enrichChildSourceNodeFromAuditRow to access
                                      window.lastExtractedBreadcrumbData = {
                                        breadcrumb: breadcrumb,
                                        videoNode: child
                                      };
                                      return breadcrumb;
                                    }
                                  } else {
                                    // Even if no short_description, try to build breadcrumb from IDs
                                    const sectionId = child.section_id_ref;
                                    const stepId = child.step_id_ref;
                                    const substepId = child.substep_id_ref;
                                    
                                    if (sectionId && stepId) {
                                      // Find the section, step, and substep in processedData to get their numbers
                                      let sectionNumber = null;
                                      let stepNumber = null;
                                      let substepLetter = null;
                                      
                                      // Find section
                                      for (let s = 0; s < processedData.length; s++) {
                                        const section = processedData[s];
                                        if (section.tree_node_id === sectionId || section.section_id_ref === sectionId) {
                                          sectionNumber = s + 1; // Sections are 1-indexed
                                          
                                          // Find step in this section
                                          if (section.steps && stepId) {
                                            for (let st = 0; st < section.steps.length; st++) {
                                              const step = section.steps[st];
                                              if (step.tree_node_id === stepId || step.step_id_ref === stepId) {
                                                stepNumber = st + 1; // Steps are 1-indexed
                                                
                                                // Find substep in this step
                                                if (step.substeps && substepId) {
                                                  for (let ss = 0; ss < step.substeps.length; ss++) {
                                                    const substep = step.substeps[ss];
                                                    if (substep.tree_node_id === substepId || substep.substep_id_ref === substepId) {
                                                      substepLetter = String.fromCharCode(65 + ss); // A=0, B=1, etc.
                                                      break;
                                                    }
                                                  }
                                                }
                                                break;
                                              }
                                            }
                                          }
                                          break;
                                        }
                                      }
                                      
                                      // Build breadcrumb from extracted numbers
                                      if (sectionNumber && stepNumber) {
                                        breadcrumb = `Section ${sectionNumber} > Step ${stepNumber}`;
                                        if (substepLetter) {
                                          breadcrumb += ` > Substep ${stepNumber}${substepLetter}`;
                                        }
                                        // Store breadcrumb globally for PreviewLayout to access
                                        window.lastExtractedBreadcrumb = breadcrumb;
                                        // Store video node data globally for enrichChildSourceNodeFromAuditRow to access
                                        window.lastExtractedBreadcrumbData = {
                                          breadcrumb: breadcrumb,
                                          videoNode: child
                                        };
                                        return breadcrumb;
                                      }
                                    }
                                  }
                                }
                              }
                            } else {
                            }
                          }
                        } else {
                        }
                      }
                    } else {
                    }
                  }
                }
                
                return breadcrumb;
              })()}
              />
            </div>
          ) : (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              <div>No content to display</div>
            </div>
          )}
        </>
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={audit_trail_data.version_id}
          nodeId={
            audit_trail_data.version_content?.element_id ||
            audit_trail_data.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={audit_trail_data.version_content?.breadcrumb_text}
          comparisonSelect
          initialFilterType="all"
          currentVersionId={audit_trail_data.version_id}
          onViewDetails={(selectedVersion) => {
            setShowVersionHistory(false);
            navigate(
              `/audit-trail/comparison/${audit_trail_id}?compareWith=${selectedVersion.version_id}`,
            );
          }}
        />
      )}
    </div>
  );
};

export default RevisionPreview;
